import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Empty,
  InputNumber,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Switch,
  Upload,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { useAuth } from '../../hooks/useAuth';
import request from '../../utils/request';
import {
  approveRechargeOrder,
  createManualFundsLedgerEntry,
  createExchangeOrder,
  createRechargeOrder,
  executeExchangeOrder,
  executeFundsFreeze,
  executeFundsUnfreeze,
  fetchFundsAccountsPaged,
  fetchFundsFreezeRecords,
  fetchFundsLedgerEntriesPaged,
  fetchRechargeOrdersPaged,
  fetchFxRateSnapshots,
  FundsAccount,
  FundsFreezeRecord,
  FundsLedgerEntry,
  FxRateSnapshot,
  RechargeOrder,
  rejectRechargeOrder,
} from '../../services/finance';
import { getCustomerList } from '../../services/project';
import type { Dayjs } from 'dayjs';

type TxStatus = 'posted' | 'void' | 'unknown';

interface FundsTransaction {
  id: string;
  reference_no: string;
  customer_id: number;
  currency: string;
  direction: 'credit' | 'debit' | 'unknown';
  type: string;
  status: TxStatus;
  amount: number;
  // 可用余额变动前/后
  balance_before: number;
  balance_after: number;
  // 冻结金额变动前/变动值/后
  frozen_before: number;
  frozen_change: number;
  frozen_after: number;
  // 总余额（可用+冻结）变动前/变动值/后
  total_before: number;
  total_change: number;
  total_after: number;
  created_at: string;
  remark?: string;
  source_label?: string;
}

type RechargeMode = 'admin' | 'client';

const BIZ_TYPE_LABEL: Record<string, string> = {
  recharge: '充值',
  payment: '账单支付',
  logistics_shipment_payment: '运费支付',
  adjustment: '调整',
  exchange_out: '兑换转出',
  exchange_in: '兑换转入',
  late_fee: '滞纳金',
  freeze: '冻结',
  unfreeze: '解冻',
};

// 交易记录表格里"变动值"列的通用渲染：正数绿色带+号，负数红色，0 用灰色（大多数记录
// 只影响可用/冻结其中一侧，另一侧的变动值天然是 0，不应该被误染成"增加"的绿色）。
const renderTxDelta = (v: number) => (
  <span style={{ color: v > 0 ? '#389e0d' : v < 0 ? '#cf1322' : '#999', fontWeight: 600 }}>
    {v > 0 ? '+' : ''}{Number(v || 0).toFixed(2)}
  </span>
);

// 冻结记录的来源类型——目前只有这两种：员工手动冻结、运费风险保证金。以后新增自动
// 冻结来源，在这里加一行映射即可，不用改其它逻辑。
const FREEZE_SOURCE_LABEL: Record<string, string> = {
  manual_freeze_control: '人工冻结',
  logistics_shipment_purchase: '运费风险保证金',
};

const MANUAL_REASON_LABEL: Record<string, string> = {
  rebate: '返利入账',
  refund: '返还款项',
  compensation: '赔付款项',
  correction: '人工纠正',
  manual_adjustment: '其他调整',
};

const STATUS_LABEL: Record<TxStatus, string> = {
  posted: '成功',
  void: '已冲销',
  unknown: '未知',
};

const STATUS_COLOR: Record<TxStatus, string> = {
  posted: 'green',
  void: 'orange',
  unknown: 'default',
};

const OFFLINE_PROVIDER_OPTIONS = [
  { label: '银行转账', value: 'bank_transfer' },
  { label: 'Zelle转账', value: 'zelle_transfer' },
  { label: 'PayPal转账', value: 'paypal_transfer' },
  { label: '支付宝转账', value: 'alipay_transfer' },
  { label: '微信转账', value: 'wechat_transfer' },
  { label: '其他', value: 'other' },
];

const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  USD: '$',
  CNY: '￥',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  HKD: 'HK$',
};

const toCurrencyCode = (currency?: string) => String(currency || '').trim().toUpperCase();

const formatCurrencyWithSymbol = (currency?: string) => {
  const code = toCurrencyCode(currency);
  if (!code) return '-';
  const symbol = CURRENCY_SYMBOL_MAP[code];
  return symbol ? `${code} ${symbol}` : code;
};

const getAvailableBalancePillStyle = (amount: number): React.CSSProperties => {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 12,
    fontWeight: 700,
  };
  if (amount < 0) {
    return {
      ...base,
      border: '1px solid #ffa39e',
      background: '#fff1f0',
      color: '#cf1322',
    };
  }
  if (Math.abs(amount) < 1e-9) {
    return {
      ...base,
      border: '1px solid #91caff',
      background: '#e6f4ff',
      color: '#0958d9',
    };
  }
  return {
    ...base,
    border: '1px solid #b7eb8f',
    background: '#f6ffed',
    color: '#389e0d',
  };
};

const buildTablePagination = (total: number, pageSize = 10) => ({
  total,
  pageSize,
  showSizeChanger: true,
  showQuickJumper: true,
  pageSizeOptions: ['10', '20', '50', '100'],
  showTotal: (t: number, range: [number, number]) => `第${range[0]}-${range[1]}条，共${t}条记录`,
});

const truncateFileName = (name: string, maxLen = 20) => {
  const text = String(name || '');
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}...`;
};

const resolveVoucherUrl = (url: string) => {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/^(data:|blob:|https?:\/\/)/i.test(raw)) return raw;
  const base = String(process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/+$/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${base}${path}`;
};

const RECHARGE_STATUS_META: Record<string, { label: string; color: string }> = {
  pending_review: { label: '待审核', color: 'gold' },
  paid: { label: '已到账', color: 'green' },
  rejected: { label: '已驳回', color: 'red' },
};

const renderRechargeStatusTag = (status?: string) => {
  const key = String(status || '').toLowerCase();
  const meta = RECHARGE_STATUS_META[key] || { label: key || '-', color: 'default' };
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

interface RechargeOrderTableProps {
  mode: RechargeMode;
  rows: RechargeOrder[];
  loading: boolean;
  total: number;
  current: number;
  pageSize: number;
  statusFilter: string;
  currencyFilter: string;
  customerFilter?: string | number;
  copyText: (text?: string) => void;
  parseVoucherFiles: (row: RechargeOrder) => Array<{ name: string; url: string; mimeType?: string }>;
  onPreviewFile: (name: string, url: string, mimeType?: string) => void;
  customerOptions?: Array<{ value: number; label: string }>;
  customerLabelMap?: Record<number, string>;
  onStatusFilterChange: (value: string) => void;
  onCurrencyFilterChange: (value: string) => void;
  onCustomerFilterChange?: (value: string | number) => void;
  onPageChange: (page: number, pageSize: number) => void;
  currencyOptions?: string[];
  onApprove?: (row: RechargeOrder) => void;
  onReject?: (row: RechargeOrder) => void;
}

const RechargeOrderTable: React.FC<RechargeOrderTableProps> = ({
  mode,
  rows,
  loading,
  total,
  current,
  pageSize,
  statusFilter,
  currencyFilter,
  customerFilter = 'all',
  copyText,
  parseVoucherFiles,
  onPreviewFile,
  customerOptions = [],
  customerLabelMap = {},
  onStatusFilterChange,
  onCurrencyFilterChange,
  onCustomerFilterChange,
  onPageChange,
  currencyOptions = [],
  onApprove,
  onReject,
}) => {
  const [showOnlyAmountDiff, setShowOnlyAmountDiff] = useState(false);

  const hasAmountDiff = useCallback((r: RechargeOrder) => {
    const statusKey = String(r.status || '').toLowerCase();
    if (statusKey !== 'paid') return false;
    const req = Number(r.requested_amount || 0);
    const settled = Number(r.settled_amount || 0);
    return Number.isFinite(req) && Number.isFinite(settled) && Math.abs(req - settled) > 1e-9;
  }, []);

  const splitRemarkAndRejectReason = useCallback((row: RechargeOrder) => {
    const explicitRejectReason = String((row as any)?.reject_reason || '').trim();
    const text = String(row?.remark || '').trim();
    if (!text) return { remarkText: '-', rejectReason: explicitRejectReason || '-' };
    const lines = text.split(/\r?\n/);
    const kept: string[] = [];
    let rejectReason = explicitRejectReason;
    lines.forEach((line) => {
      const ln = String(line || '').trim();
      const m = ln.match(/^驳回原因[:：]\s*(.*)$/);
      if (m) {
        rejectReason = String(m[1] || '').trim() || rejectReason;
      } else if (ln) {
        kept.push(ln);
      }
    });
    return {
      remarkText: kept.length ? kept.join('\n') : '-',
      rejectReason: rejectReason || '-',
    };
  }, []);

  const statusFilterOptions = useMemo(() => {
    return [
      { value: 'all', label: '全部状态' },
      ...['pending_review', 'paid', 'rejected'].map((k) => ({
        value: k,
        label: RECHARGE_STATUS_META[k]?.label || k,
      })),
    ];
  }, []);

  const commonColumns: any[] = [
    {
      title: '申报单号',
      dataIndex: 'order_no',
      width: 200,
      render: (v: string) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <Typography.Text code>{v}</Typography.Text>
          <CopyOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => copyText(v)} />
        </span>
      ),
    },
    { title: '币种', dataIndex: 'currency', width: 90, render: (v: string) => formatCurrencyWithSymbol(v) },
    { title: '申报金额', dataIndex: 'requested_amount', width: 110, align: 'right', render: (v: any) => Number(v || 0).toFixed(2) },
    {
      title: '实际到账金额',
      dataIndex: 'settled_amount',
      width: 140,
      align: 'right',
      render: (_: any, r: RechargeOrder) => {
        const statusKey = String(r.status || '').toLowerCase();
        if (statusKey !== 'paid') return '-';
        const req = Number(r.requested_amount || 0);
        const settled = Number(r.settled_amount || 0);
        const hasDiff = hasAmountDiff(r);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
            <span>{settled.toFixed(2)}</span>
            {hasDiff ? (
              <Tooltip title="实际到账与申报金额不同">
                <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
              </Tooltip>
            ) : null}
          </span>
        );
      },
    },
    { title: '渠道', dataIndex: 'provider', width: 120, render: (v: string) => OFFLINE_PROVIDER_OPTIONS.find((o) => o.value === v)?.label || v || '-' },
    { title: '流水号', dataIndex: 'provider_trade_no', width: 150, render: (v: string) => v || '-' },
    {
      title: '付款凭证文件',
      dataIndex: 'voucher_image_url',
      width: 220,
      render: (_: any, r: RechargeOrder) => {
        const files = parseVoucherFiles(r);
        if (!files.length) return '-';
        return (
          <Space direction="vertical" size={2}>
            {files.map((f, idx) => (
              <a
                key={`${f.url}-${idx}`}
                title={f.name}
                onClick={(e) => {
                  e.preventDefault();
                  onPreviewFile(f.name, f.url, f.mimeType);
                }}
              >
                {truncateFileName(f.name, 20)}
              </a>
            ))}
          </Space>
        );
      },
    },
    { title: '状态', dataIndex: 'status', width: 120, render: (v: string) => renderRechargeStatusTag(v) },
    { title: '提交时间', dataIndex: 'created_at', width: 170, render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-') },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 220,
      ellipsis: true,
      render: (_: any, r: RechargeOrder) => splitRemarkAndRejectReason(r).remarkText,
    },
    {
      title: '驳回原因',
      dataIndex: 'reject_reason',
      width: 220,
      ellipsis: true,
      render: (_: any, r: RechargeOrder) => splitRemarkAndRejectReason(r).rejectReason,
    },
  ];

  const columns = useMemo(() => {
    const cols = [...commonColumns];
    if (mode === 'admin') {
      cols.splice(1, 0, {
        title: '所属客户',
        dataIndex: 'customer_id',
        width: 200,
        render: (v: number) => customerLabelMap?.[Number(v)] || `客户#${v}`,
      });
      cols.push({
        title: '操作',
        key: 'actions',
        width: 140,
        render: (_: any, r: RechargeOrder) => (
          <Space size={4}>
            <Button
              size="small"
              type="link"
              disabled={!['pending_review'].includes(String(r.status || '').toLowerCase())}
              onClick={() => onApprove?.(r)}
            >
              通过
            </Button>
            <Button
              size="small"
              type="link"
              danger
              disabled={!['pending_review'].includes(String(r.status || '').toLowerCase())}
              onClick={() => onReject?.(r)}
            >
              驳回
            </Button>
          </Space>
        ),
      });
    } else {
      const remarkColIndex = cols.findIndex((c) => c?.title === '备注');
      const insertAt = remarkColIndex >= 0 ? remarkColIndex : cols.length - 1;
      // 客户侧不再显示审核时间列
    }
    return cols;
  }, [commonColumns, mode, onApprove, onReject, customerLabelMap]);

  const displayRows = useMemo(() => {
    if (!showOnlyAmountDiff) return rows;
    return rows.filter(hasAmountDiff);
  }, [rows, showOnlyAmountDiff, hasAmountDiff]);

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      <Space size={8} wrap>
        {mode === 'admin' ? (
          <>
            <Typography.Text type="secondary">所属客户</Typography.Text>
            <Select
              size="small"
              style={{ width: 240 }}
              value={customerFilter}
              onChange={(v) => onCustomerFilterChange?.(v)}
              options={[
                { value: 'all', label: '全部客户' },
                ...(customerOptions || []),
              ]}
            />
          </>
        ) : null}
        <Typography.Text type="secondary">币种</Typography.Text>
        <Select
          size="small"
          style={{ width: 160 }}
          value={currencyFilter}
          onChange={(v) => onCurrencyFilterChange(String(v))}
          options={[
            { value: 'all', label: '全部币种' },
            ...currencyOptions.map((c) => ({ value: c, label: formatCurrencyWithSymbol(c) })),
          ]}
        />
        <Typography.Text type="secondary">申报状态</Typography.Text>
        <Select
          size="small"
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(v) => onStatusFilterChange(String(v))}
          options={statusFilterOptions}
        />
        <Switch
          checked={showOnlyAmountDiff}
          onChange={(checked) => {
            setShowOnlyAmountDiff(checked);
            if (checked && statusFilter !== 'all') {
              onStatusFilterChange('all');
            }
            if (checked && currencyFilter !== 'all') {
              onCurrencyFilterChange('all');
            }
          }}
          checkedChildren="有误差"
          unCheckedChildren="全部"
        />
        <Typography.Text type="secondary">仅显示实际到账与申报金额有误差的记录</Typography.Text>
      </Space>
      <Table<RechargeOrder>
        rowKey={(r) => String(r.id)}
        size="small"
        loading={loading}
        dataSource={displayRows}
        onRow={(record) => {
          const statusKey = String(record.status || '').toLowerCase();
          if (statusKey === 'paid') {
            return { style: { backgroundColor: '#f6ffed' } };
          }
          if (statusKey === 'rejected') {
            return { style: { backgroundColor: '#fff1f0' } };
          }
          return {};
        }}
        pagination={{
          ...buildTablePagination(showOnlyAmountDiff ? displayRows.length : total, pageSize),
          current,
          onChange: onPageChange,
        }}
        columns={columns}
        scroll={{ x: mode === 'admin' ? 1900 : 1700 }}
      />
    </Space>
  );
};

const FundsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdminPath = typeof window !== 'undefined' && window.location.pathname.includes('/dashboard/admin/');
  const initialTab = useMemo(() => {
    if (typeof window === 'undefined') return 'accounts';
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (tab === 'accounts' || tab === 'recharge' || tab === 'transactions') return tab;
    return 'accounts';
  }, []);
  const [activeTabKey, setActiveTabKey] = useState<string>(initialTab);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<FundsAccount[]>([]);
  const [accountTotal, setAccountTotal] = useState(0);
  const [accountPage, setAccountPage] = useState(1);
  const [accountPageSize, setAccountPageSize] = useState(20);
  const [adminCurrencyList, setAdminCurrencyList] = useState<string[]>([]);
  const [adminCurrencyRows, setAdminCurrencyRows] = useState<Record<string, FundsAccount[]>>({});
  const [adminCurrencyTotals, setAdminCurrencyTotals] = useState<Record<string, number>>({});
  const [adminCurrencySummary, setAdminCurrencySummary] = useState<Record<string, { customerCount: number; totalBalance: number }>>({});
  const [adminCurrencyPages, setAdminCurrencyPages] = useState<Record<string, { page: number; pageSize: number }>>({});
  const [adminCurrencyFilter, setAdminCurrencyFilter] = useState<string>('all');
  const [adminNegativeOnly, setAdminNegativeOnly] = useState(false);
  const [transactions, setTransactions] = useState<FundsTransaction[]>([]);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(20);
  const [transactionCustomerFilter, setTransactionCustomerFilter] = useState<string | number>('all');
  const [transactionCurrencyFilter, setTransactionCurrencyFilter] = useState<string>('all');
  const [transactionDirectionFilter, setTransactionDirectionFilter] = useState<string>('all');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('all');
  const [transactionFuzzyFilter, setTransactionFuzzyFilter] = useState<string>('');
  const [transactionDateRange, setTransactionDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [transactionSearchTick, setTransactionSearchTick] = useState(0);
  const [transactionExporting, setTransactionExporting] = useState(false);
  const [transactionHelpOpen, setTransactionHelpOpen] = useState(false);
  const [manualLedgerOpen, setManualLedgerOpen] = useState(false);
  const [manualLedgerConfirmOpen, setManualLedgerConfirmOpen] = useState(false);
  const [manualLedgerSubmitting, setManualLedgerSubmitting] = useState(false);
  const [manualLedgerCustomerId, setManualLedgerCustomerId] = useState<number | null>(null);
  const [manualLedgerCurrency, setManualLedgerCurrency] = useState<string>('USD');
  const [manualLedgerDirection, setManualLedgerDirection] = useState<'credit' | 'debit'>('credit');
  const [manualLedgerAmount, setManualLedgerAmount] = useState<number | null>(null);
  const [manualLedgerRemark, setManualLedgerRemark] = useState('');
  const [manualLedgerReasonType, setManualLedgerReasonType] = useState('manual_adjustment');
  const [manualLedgerAccounts, setManualLedgerAccounts] = useState<FundsAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [manualCustomerId, setManualCustomerId] = useState<number | null>(null);
  const [freezeControlOpen, setFreezeControlOpen] = useState(false);
  const [freezeControlSubmitting, setFreezeControlSubmitting] = useState(false);
  const [freezeControlAccount, setFreezeControlAccount] = useState<FundsAccount | null>(null);
  const [freezeControlAction, setFreezeControlAction] = useState<'freeze' | 'unfreeze'>('freeze');
  const [freezeControlAmount, setFreezeControlAmount] = useState<number | null>(null);
  const [freezeControlRemark, setFreezeControlRemark] = useState('');
  // 解冻不再是填一个金额，而是从这个账户当前"冻结中"的记录里勾选要解冻哪几条。
  const [unfreezeCandidates, setUnfreezeCandidates] = useState<FundsFreezeRecord[]>([]);
  const [unfreezeCandidatesLoading, setUnfreezeCandidatesLoading] = useState(false);
  const [selectedUnfreezeIds, setSelectedUnfreezeIds] = useState<number[]>([]);

  // "查看冻结明细"：展示某个账户全部冻结记录（冻结中+已解冻），跟解冻弹窗共用同一个
  // 后端接口，只是不筛 status，也不能勾选操作。
  const [freezeDetailOpen, setFreezeDetailOpen] = useState(false);
  const [freezeDetailAccount, setFreezeDetailAccount] = useState<FundsAccount | null>(null);
  const [freezeDetailRecords, setFreezeDetailRecords] = useState<FundsFreezeRecord[]>([]);
  const [freezeDetailLoading, setFreezeDetailLoading] = useState(false);
  const [freezeDetailPage, setFreezeDetailPage] = useState(1);
  const [freezeDetailPageSize, setFreezeDetailPageSize] = useState(10);
  const [freezeDetailTotal, setFreezeDetailTotal] = useState(0);
  const [freezeDetailDownloading, setFreezeDetailDownloading] = useState(false);

  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState<number | null>(null);
  const [rechargeCurrency, setRechargeCurrency] = useState<string>('USD');
  const [rechargeProvider, setRechargeProvider] = useState<string>('bank_transfer');
  const [rechargeTradeNo, setRechargeTradeNo] = useState<string>('');
  const [rechargeFileList, setRechargeFileList] = useState<UploadFile[]>([]);
  const [rechargeRemark, setRechargeRemark] = useState<string>('');
  const [rechargeCustomerId, setRechargeCustomerId] = useState<number | null>(null);
  const [rechargeSubmitting, setRechargeSubmitting] = useState(false);
  const [rechargeCustomerOptions, setRechargeCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [rechargeCustomerLoading, setRechargeCustomerLoading] = useState(false);
  const [adminCustomerOptions, setAdminCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [customerLabelMap, setCustomerLabelMap] = useState<Record<number, string>>({});
  const [reviewRows, setReviewRows] = useState<RechargeOrder[]>([]);
  const [clientApplyRows, setClientApplyRows] = useState<RechargeOrder[]>([]);
  const [rechargeTotal, setRechargeTotal] = useState(0);
  const [rechargePage, setRechargePage] = useState(1);
  const [rechargePageSize, setRechargePageSize] = useState(20);
  const [rechargeStatusFilter, setRechargeStatusFilter] = useState<string>(isAdminPath ? 'pending_review' : 'all');
  const [rechargeCurrencyFilter, setRechargeCurrencyFilter] = useState<string>('all');
  const [rechargeCustomerFilter, setRechargeCustomerFilter] = useState<string | number>('all');
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [approveTarget, setApproveTarget] = useState<RechargeOrder | null>(null);
  const [approveAmount, setApproveAmount] = useState<number | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<RechargeOrder | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [exchangeSubmitting, setExchangeSubmitting] = useState(false);
  const [exchangeCustomerId, setExchangeCustomerId] = useState<number | null>(null);
  const [exchangeAccounts, setExchangeAccounts] = useState<FundsAccount[]>([]);
  const [exchangeAccountsLoading, setExchangeAccountsLoading] = useState(false);
  const [exchangeSourceAccountId, setExchangeSourceAccountId] = useState<number | null>(null);
  const [exchangeTargetAccountId, setExchangeTargetAccountId] = useState<number | null>(null);
  const [exchangeFromAmount, setExchangeFromAmount] = useState<number | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [exchangeRateSource, setExchangeRateSource] = useState<'manual' | 'snapshot'>('manual');
  const [fxSnapshots, setFxSnapshots] = useState<FxRateSnapshot[]>([]);
  const [selectedFxSnapshotId, setSelectedFxSnapshotId] = useState<number | null>(null);
  const [voucherPreviewOpen, setVoucherPreviewOpen] = useState(false);
  const [voucherPreviewSrc, setVoucherPreviewSrc] = useState<string>('');
  const [voucherPreviewTitle, setVoucherPreviewTitle] = useState<string>('');
  const [voucherPreviewRawUrl, setVoucherPreviewRawUrl] = useState<string>('');
  const voucherPreviewBlobUrlRef = useRef<string>('');

  const copyText = async (text?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  const parseVoucherFiles = (row: RechargeOrder): Array<{ name: string; url: string; mimeType?: string }> => {
    const meta: any = (row as any)?.meta || {};
    const itemList = Array.isArray(meta?.voucher_file_items) ? meta.voucher_file_items : [];
    if (itemList.length) {
      return itemList
        .map((it: any) => ({
          name: String(it?.name || '').trim(),
          url: String(it?.url || '').trim(),
          mimeType: String(it?.mime_type || '').trim(),
        }))
        .filter((it: any) => !!it.url)
        .map((it: any, idx: number) => ({
          name: it.name || `凭证文件${idx + 1}`,
          url: it.url,
          mimeType: it.mimeType || '',
        }));
    }
    const urls: string[] = Array.isArray(meta?.voucher_image_urls) && meta.voucher_image_urls.length
      ? meta.voucher_image_urls
      : (row?.voucher_image_url ? [row.voucher_image_url] : []);
    return urls
      .map((u) => String(u || '').trim())
      .filter(Boolean)
      .map((u, idx) => {
        const raw = u.split('?')[0];
        const seg = raw.split('/').pop() || '';
        let name = '';
        try {
          name = decodeURIComponent(seg);
        } catch {
          name = seg;
        }
        return {
          name: name || `凭证文件${idx + 1}`,
          url: u,
          mimeType: '',
        };
      });
  };
  const [voucherPreviewType, setVoucherPreviewType] = useState<'image' | 'pdf' | 'unsupported'>('unsupported');

  const revokeVoucherPreviewBlobUrl = useCallback(() => {
    if (voucherPreviewBlobUrlRef.current) {
      URL.revokeObjectURL(voucherPreviewBlobUrlRef.current);
      voucherPreviewBlobUrlRef.current = '';
    }
  }, []);

  const closeVoucherPreview = useCallback(() => {
    setVoucherPreviewOpen(false);
    revokeVoucherPreviewBlobUrl();
  }, [revokeVoucherPreviewBlobUrl]);

  useEffect(() => {
    return () => {
      revokeVoucherPreviewBlobUrl();
    };
  }, [revokeVoucherPreviewBlobUrl]);

  const allVisibleAccounts = useMemo(() => {
    if (!isAdminPath) return accounts;
    return Object.values(adminCurrencyRows).flat();
  }, [accounts, adminCurrencyRows, isAdminPath]);

  const selectedAccount = useMemo(
    () => allVisibleAccounts.find((x) => x.id === selectedAccountId) || allVisibleAccounts[0] || null,
    [allVisibleAccounts, selectedAccountId]
  );

  const mapLedgerToTx = useCallback((rows: FundsLedgerEntry[]): FundsTransaction[] => {
    return rows.map((r) => {
      const amt = Number(r.amount || 0);
      const signAmount = r.direction === 'debit' ? -amt : amt;
      const rawStatus = (r.status || '').toLowerCase();
      const status: TxStatus = rawStatus === 'posted' || rawStatus === 'void' ? (rawStatus as TxStatus) : 'unknown';
      const rawDirection = String((r as any).direction || '').toLowerCase();
      const direction: 'credit' | 'debit' | 'unknown' =
        rawDirection === 'credit' || rawDirection === 'debit' ? (rawDirection as 'credit' | 'debit') : 'unknown';
      // 可用/冻结的变动前都用后端已经落库的 delta_* 反推（balance_after - delta），不用自己
      // 重新猜测方向——每条记录自己就是完整闭环的 before+change=after，不依赖相邻记录。
      const deltaAvailable = Number(r.delta_available ?? signAmount ?? 0);
      const deltaFrozen = Number(r.delta_frozen || 0);
      const afterAvailable = Number(r.balance_after_available || 0);
      const afterFrozen = Number(r.balance_after_frozen || 0);
      const beforeAvailable = afterAvailable - deltaAvailable;
      const beforeFrozen = afterFrozen - deltaFrozen;
      return {
        id: String(r.id),
        reference_no: r.entry_no,
        customer_id: Number((r as any).customer_id || 0),
        currency: r.currency,
        direction,
        type: r.biz_type,
        status,
        amount: signAmount,
        balance_before: beforeAvailable,
        balance_after: afterAvailable,
        frozen_before: beforeFrozen,
        frozen_change: deltaFrozen,
        frozen_after: afterFrozen,
        // 总余额 = 可用 + 冻结：单纯的冻结/解冻这类记录理论上总余额不变（钱只是换了个
        // "能不能立即用"的状态），只有运费支付/调整/充值这类记录才会让总余额真的变化。
        total_before: beforeAvailable + beforeFrozen,
        total_change: deltaAvailable + deltaFrozen,
        total_after: afterAvailable + afterFrozen,
        created_at: r.created_at,
        remark: r.remark || undefined,
        source_label: r.source_label || undefined,
      };
    });
  }, []);

  const buildTransactionQueryParams = useCallback(() => {
    const myCustomerId = Number(user?.id || 0) || undefined;
    const params: Record<string, any> = {};
    if (isAdminPath) {
      if (transactionCustomerFilter !== 'all') params.customer_id = Number(transactionCustomerFilter);
    } else {
      params.customer_id = myCustomerId;
    }
    if (transactionCurrencyFilter && transactionCurrencyFilter !== 'all') {
      params.currency = transactionCurrencyFilter;
    }
    if (transactionDirectionFilter && transactionDirectionFilter !== 'all') {
      params.direction = transactionDirectionFilter;
    }
    if (transactionTypeFilter && transactionTypeFilter !== 'all') {
      params.biz_type = transactionTypeFilter;
    }
    if (transactionFuzzyFilter.trim()) {
      params.fuzzy_keyword = transactionFuzzyFilter.trim();
    }
    if (transactionDateRange?.[0]) {
      params.created_at_after = transactionDateRange[0].startOf('day').format('YYYY-MM-DD');
    }
    if (transactionDateRange?.[1]) {
      params.created_at_before = transactionDateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [
    isAdminPath,
    transactionCurrencyFilter,
    transactionCustomerFilter,
    transactionDateRange,
    transactionDirectionFilter,
    transactionFuzzyFilter,
    transactionTypeFilter,
    user?.id,
  ]);

  const loadTransactions = useCallback(async () => {
    const params: any = {
      ...buildTransactionQueryParams(),
      page: transactionPage,
      page_size: transactionPageSize,
    };
    const resp = await fetchFundsLedgerEntriesPaged(params);
    setTransactions(mapLedgerToTx(resp.results || []));
    setTransactionTotal(Number(resp.count || 0));
  }, [
    buildTransactionQueryParams,
    mapLedgerToTx,
    transactionPage,
    transactionPageSize,
    transactionSearchTick,
  ]);

  useEffect(() => {
    loadTransactions().catch(() => undefined);
  }, [loadTransactions]);

  const loadRechargeOrders = useCallback(async () => {
    const myCustomerId = Number(user?.id || 0) || undefined;
    const params: any = {
      channel: 'offline',
      page: rechargePage,
      page_size: rechargePageSize,
    };
    if (rechargeCurrencyFilter && rechargeCurrencyFilter !== 'all') params.currency = rechargeCurrencyFilter;
    if (rechargeStatusFilter && rechargeStatusFilter !== 'all') params.status = rechargeStatusFilter;
    if (isAdminPath) {
      if (rechargeCustomerFilter !== 'all') params.customer_id = Number(rechargeCustomerFilter);
      const reviewResp = await fetchRechargeOrdersPaged(params);
      setReviewRows(reviewResp.results || []);
      setClientApplyRows([]);
      setRechargeTotal(Number(reviewResp.count || 0));
    } else {
      params.customer_id = myCustomerId;
      const clientResp = await fetchRechargeOrdersPaged(params);
      setReviewRows([]);
      setClientApplyRows(clientResp.results || []);
      setRechargeTotal(Number(clientResp.count || 0));
    }
  }, [isAdminPath, rechargeCurrencyFilter, rechargeCustomerFilter, rechargePage, rechargePageSize, rechargeStatusFilter, user?.id]);

  const loadAdminCurrencyAccounts = useCallback(async (currencyCode: string, page: number, pageSize: number) => {
    if (!adminNegativeOnly) {
      const resp = await fetchFundsAccountsPaged({
        customer_id: manualCustomerId || undefined,
        currency: currencyCode,
        page,
        page_size: pageSize,
      });
      setAdminCurrencyRows((prev) => ({ ...prev, [currencyCode]: resp.results || [] }));
      setAdminCurrencyTotals((prev) => ({ ...prev, [currencyCode]: Number(resp.count || 0) }));
      return;
    }

    let loaded = 0;
    let queryPage = 1;
    const queryPageSize = 200;
    let totalCount = 0;
    const negatives: FundsAccount[] = [];
    while (queryPage <= 50) {
      const resp = await fetchFundsAccountsPaged({
        customer_id: manualCustomerId || undefined,
        currency: currencyCode,
        page: queryPage,
        page_size: queryPageSize,
      });
      const rows = resp.results || [];
      totalCount = Number(resp.count || 0);
      rows.forEach((row) => {
        if (Number(row.available_balance || 0) < 0) negatives.push(row);
      });
      loaded += rows.length;
      if (!rows.length || loaded >= totalCount) break;
      queryPage += 1;
    }
    const start = (page - 1) * pageSize;
    const pageRows = negatives.slice(start, start + pageSize);
    setAdminCurrencyRows((prev) => ({ ...prev, [currencyCode]: pageRows }));
    setAdminCurrencyTotals((prev) => ({ ...prev, [currencyCode]: negatives.length }));
  }, [adminNegativeOnly, manualCustomerId]);

  const loadAccounts = useCallback(async () => {
    const myCustomerId = Number(user?.id || 0) || undefined;
    if (isAdminPath) {
      const currencySet = new Set<string>();
      const summaryMap = new Map<string, { customers: Set<number>; totalBalance: number }>();
      let seedRows: FundsAccount[] = [];
      let totalCount = 0;
      let loaded = 0;
      let page = 1;
      const seedPageSize = 200;
      while (page <= 50) {
        const pageResp = await fetchFundsAccountsPaged({
          customer_id: manualCustomerId || undefined,
          page,
          page_size: seedPageSize,
        });
        const rows = pageResp.results || [];
        if (page === 1) seedRows = rows;
        totalCount = Number(pageResp.count || 0);
        rows.forEach((x) => {
          if (adminNegativeOnly && Number(x.available_balance || 0) >= 0) return;
          const code = String(x.currency || '').toUpperCase();
          if (!code) return;
          currencySet.add(code);
          const old = summaryMap.get(code) || { customers: new Set<number>(), totalBalance: 0 };
          old.customers.add(Number(x.customer_id || 0));
          old.totalBalance += Number(x.available_balance || 0) + Number(x.frozen_balance || 0);
          summaryMap.set(code, old);
        });
        loaded += rows.length;
        if (!rows.length || loaded >= totalCount) break;
        page += 1;
      }
      const currencies = Array.from(currencySet).sort();
      const summaryObj: Record<string, { customerCount: number; totalBalance: number }> = {};
      currencies.forEach((cur) => {
        const item = summaryMap.get(cur);
        summaryObj[cur] = {
          customerCount: item ? item.customers.size : 0,
          totalBalance: item ? Number(item.totalBalance || 0) : 0,
        };
      });
      setAdminCurrencySummary(summaryObj);
      setAdminCurrencyList(currencies);
      if (currencies.length === 0) {
        setAdminCurrencyRows({});
        setAdminCurrencyTotals({});
        setAdminCurrencySummary({});
        setAccounts([]);
        setAccountTotal(0);
        return;
      }
      const nextPages: Record<string, { page: number; pageSize: number }> = {};
      currencies.forEach((cur) => {
        const old = adminCurrencyPages[cur];
        nextPages[cur] = old || { page: 1, pageSize: 20 };
      });
      const hasSamePages =
        Object.keys(adminCurrencyPages).length === Object.keys(nextPages).length &&
        Object.entries(nextPages).every(([cur, p]) => {
          const old = adminCurrencyPages[cur];
          return !!old && old.page === p.page && old.pageSize === p.pageSize;
        });
      if (!hasSamePages) {
        setAdminCurrencyPages(nextPages);
      }
      await Promise.all(
        currencies.map((cur) => loadAdminCurrencyAccounts(cur, nextPages[cur].page, nextPages[cur].pageSize))
      );
      setAccountTotal(totalCount);
      setAccounts(seedRows);
      return;
    }
    const resp = await fetchFundsAccountsPaged({
      customer_id: myCustomerId,
      page: accountPage,
      page_size: accountPageSize,
    });
    const rows = resp.results || [];
    setAccounts(rows);
    setAccountTotal(Number(resp.count || 0));
    setAdminCurrencyList([]);
    setAdminCurrencyRows({});
    setAdminCurrencyTotals({});
    setAdminCurrencySummary({});
  }, [
    accountPage,
    accountPageSize,
    adminCurrencyPages,
    adminNegativeOnly,
    isAdminPath,
    loadAdminCurrencyAccounts,
    manualCustomerId,
    user?.id,
  ]);

  const loadExchangeAccounts = useCallback(async () => {
    let targetCustomerId: number | undefined;
    if (isAdminPath) {
      targetCustomerId = exchangeCustomerId ? Number(exchangeCustomerId) : undefined;
    } else {
      targetCustomerId = Number(user?.id || 0) || undefined;
    }
    setExchangeAccountsLoading(true);
    try {
      const resp = await fetchFundsAccountsPaged({
        customer_id: targetCustomerId,
        page: 1,
        page_size: 300,
      });
      const rows = resp.results || [];
      setExchangeAccounts(rows);
      const first = rows[0] || null;
      setExchangeSourceAccountId((prev) => (prev && rows.some((r) => r.id === prev) ? prev : first?.id || null));
      setExchangeTargetAccountId((prev) => (prev && rows.some((r) => r.id === prev) ? prev : null));
    } finally {
      setExchangeAccountsLoading(false);
    }
  }, [exchangeCustomerId, isAdminPath, user?.id]);

  const loadCoreData = useCallback(async () => {
    setLoading(true);
    try {
      await loadAccounts();
      await loadRechargeOrders();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '加载资金数据失败');
    } finally {
      setLoading(false);
    }
  }, [loadAccounts, loadRechargeOrders]);

  const refreshAllTabsData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAccounts(),
        loadRechargeOrders(),
        loadTransactions(),
        loadExchangeAccounts(),
      ]);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '加载资金数据失败');
    } finally {
      setLoading(false);
    }
  }, [loadAccounts, loadRechargeOrders, loadTransactions, loadExchangeAccounts]);

  useEffect(() => {
    loadCoreData();
  }, [loadCoreData]);

  const selectedExchangeSourceAccount = useMemo(
    () => exchangeAccounts.find((x) => x.id === exchangeSourceAccountId) || null,
    [exchangeAccounts, exchangeSourceAccountId]
  );

  const exchangeTargetCandidates = useMemo(() => {
    if (!selectedExchangeSourceAccount) return [];
    return exchangeAccounts.filter(
      (x) =>
        x.customer_id === selectedExchangeSourceAccount.customer_id &&
        x.id !== selectedExchangeSourceAccount.id &&
        String(x.currency || '').toUpperCase() !== String(selectedExchangeSourceAccount.currency || '').toUpperCase()
    );
  }, [exchangeAccounts, selectedExchangeSourceAccount]);

  const selectedTargetAccount = useMemo(
    () => exchangeTargetCandidates.find((x) => x.id === exchangeTargetAccountId) || null,
    [exchangeTargetCandidates, exchangeTargetAccountId]
  );

  const fxCandidates = useMemo(() => {
    if (!selectedExchangeSourceAccount || !selectedTargetAccount) return [];
    const from = String(selectedExchangeSourceAccount.currency || '').toUpperCase();
    const to = String(selectedTargetAccount.currency || '').toUpperCase();
    return fxSnapshots.filter(
      (x) =>
        String(x.from_currency || '').toUpperCase() === from &&
        String(x.to_currency || '').toUpperCase() === to
    );
  }, [fxSnapshots, selectedExchangeSourceAccount, selectedTargetAccount]);

  const getBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = (error) => reject(error);
    });

  const openVoucherPreview = async (file: UploadFile) => {
    revokeVoucherPreviewBlobUrl();
    const src =
      file.url ||
      (file.preview as string) ||
      (file.originFileObj ? await getBase64(file.originFileObj as File) : '');
    if (!src) {
      message.warning('该文件暂不支持预览');
      return;
    }
    const lowerName = String(file.name || '').toLowerCase();
    const isPdfByName = lowerName.endsWith('.pdf');
    const isPdfByData = src.startsWith('data:application/pdf');
    const isImageByData = src.startsWith('data:image/');
    const isImageByName = /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(lowerName);
    const isPdf = isPdfByName || isPdfByData || /[?&]format=pdf/i.test(src);
    const isImage = isImageByData || isImageByName;

    setVoucherPreviewSrc(src);
    setVoucherPreviewRawUrl(src);
    setVoucherPreviewTitle(file.name || '付款凭证预览');
    setVoucherPreviewType(isPdf ? 'pdf' : isImage ? 'image' : 'unsupported');
    setVoucherPreviewOpen(true);
  };

  const openVoucherPreviewByNameUrl = async (name: string, url: string, mimeType?: string) => {
    revokeVoucherPreviewBlobUrl();
    const raw = String(url || '').trim();
    const resolved = resolveVoucherUrl(raw);
    if (!resolved) {
      message.warning('该文件暂不支持预览');
      return;
    }
    const lowerName = String(name || '').toLowerCase();
    const lowerMime = String(mimeType || '').toLowerCase();
    const isPdfByName = lowerName.endsWith('.pdf') || /[?&]format=pdf/i.test(resolved);
    const isPdfByMime = lowerMime.includes('application/pdf');
    const isImageByName = /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(lowerName);
    const isImageByMime = lowerMime.startsWith('image/');
    let src = resolved;
    let detectedType: 'image' | 'pdf' | 'unsupported' = 'unsupported';
    if (/^(data:|blob:)/i.test(resolved)) {
      const isPdf = isPdfByName || isPdfByMime || resolved.startsWith('data:application/pdf');
      const isImage = isImageByName || isImageByMime || resolved.startsWith('data:image/');
      detectedType = isPdf ? 'pdf' : isImage ? 'image' : 'unsupported';
    } else {
      try {
        const resp = await request.get(resolved, { responseType: 'blob' as any });
        const blob: Blob = resp?.data instanceof Blob ? resp.data : new Blob([resp?.data]);
        src = URL.createObjectURL(blob);
        voucherPreviewBlobUrlRef.current = src;
        const blobType = String(blob.type || '').toLowerCase();
        const isPdf = isPdfByName || isPdfByMime || blobType.includes('application/pdf');
        const isImage = isImageByName || isImageByMime || blobType.startsWith('image/');
        detectedType = isPdf ? 'pdf' : isImage ? 'image' : 'unsupported';
      } catch {
        message.error('预览加载失败，请尝试下载文件');
        return;
      }
    }
    setVoucherPreviewSrc(src);
    setVoucherPreviewRawUrl(raw);
    setVoucherPreviewTitle(name || '付款凭证预览');
    setVoucherPreviewType(detectedType);
    setVoucherPreviewOpen(true);
  };

  const downloadVoucherFile = async () => {
    const fileName = voucherPreviewTitle || '付款凭证';
    const raw = String(voucherPreviewRawUrl || voucherPreviewSrc || '').trim();
    if (!raw) return;
    const resolved = resolveVoucherUrl(raw);
    try {
      if (/^(data:|blob:)/i.test(resolved)) {
        const a = document.createElement('a');
        a.href = resolved;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }
      const resp = await request.get(resolved, { responseType: 'blob' as any });
      const blob = new Blob([resp.data]);
      const obj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = obj;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(obj);
    } catch {
      message.error('下载失败');
    }
  };

  const rechargeCustomerReady = isAdminPath ? !!rechargeCustomerId : !!user?.id;
  const canSubmitRecharge =
    rechargeCustomerReady &&
    !!String(rechargeCurrency || '').trim() &&
    !!String(rechargeProvider || '').trim() &&
    Number(rechargeAmount || 0) > 0 &&
    rechargeFileList.length > 0;

  const doRecharge = async () => {
    const amount = Number(rechargeAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      message.warning('请输入有效充值金额');
      return;
    }
    if (!rechargeFileList.length) {
      message.warning('请上传付款凭证');
      return;
    }
    const voucherFiles = rechargeFileList
      .map((f) => f.originFileObj)
      .filter((f): f is RcFile => !!f)
      .slice(0, 3);
    if (!voucherFiles.length) {
      message.warning('请上传付款凭证');
      return;
    }
    let targetCustomerId: number | null = null;
    if (isAdminPath) {
      targetCustomerId = Number(rechargeCustomerId || manualCustomerId || 0) || null;
    } else {
      targetCustomerId = Number(user?.id || 0) || null;
    }
    if (!targetCustomerId) {
      message.warning('请先指定客户ID');
      return;
    }
    setRechargeSubmitting(true);
    try {
      await createRechargeOrder({
        customer_id: Number(targetCustomerId),
        currency: String(rechargeCurrency || 'USD').toUpperCase(),
        requested_amount: amount,
        channel: 'offline',
        provider: rechargeProvider,
        provider_trade_no: rechargeTradeNo || undefined,
        voucher_files: voucherFiles as File[],
        status: 'pending_review',
        remark: rechargeRemark || undefined,
      });
      setRechargeOpen(false);
      setRechargeAmount(null);
      setRechargeTradeNo('');
      setRechargeFileList([]);
      setRechargeRemark('');
      setRechargeCustomerId(null);
      message.success('线下到账申报已提交，等待管理员审核');
      await refreshAllTabsData();
    } finally {
      setRechargeSubmitting(false);
    }
  };

  const searchRechargeCustomers = useCallback(async (kw?: string) => {
    if (!isAdminPath) return;
    setRechargeCustomerLoading(true);
    try {
      const list: any[] = (await getCustomerList({ onlyMain: true })) || [];
      const keyword = String(kw || '').trim().toLowerCase();
      const filtered = keyword
        ? list.filter((c: any) => {
            const nm = String(c?.name || c?.username || '').toLowerCase();
            const code = String(c?.customer_code || '').toLowerCase();
            return nm.includes(keyword) || code.includes(keyword);
          })
        : list;
      const opts = filtered.map((c: any) => {
        const name = c?.name || c?.username || '';
        const code = c?.customer_code || '';
        const label = code ? `${name} · ${code}` : `${name}`;
        return { value: Number(c.id), label };
      });
      setRechargeCustomerOptions(opts);
    } catch {
      setRechargeCustomerOptions([]);
    } finally {
      setRechargeCustomerLoading(false);
    }
  }, [isAdminPath, user?.id]);

  useEffect(() => {
    const run = async () => {
      if (!isAdminPath) return;
      try {
        const list: any[] = (await getCustomerList({ onlyMain: true })) || [];
        const options = list.map((c: any) => {
          const id = Number(c?.id);
          const name = String(c?.name || c?.username || '').trim();
          const code = String(c?.customer_code || '').trim();
          const label = code ? `${name} · ${code}` : name || `客户#${id}`;
          return { value: id, label };
        }).filter((x: any) => Number.isFinite(x.value));
        const map: Record<number, string> = {};
        options.forEach((x) => { map[x.value] = x.label; });
        setAdminCustomerOptions(options);
        setCustomerLabelMap(map);
      } catch {
        setAdminCustomerOptions([]);
        setCustomerLabelMap({});
      }
    };
    run();
  }, [isAdminPath]);

  useEffect(() => {
    setRechargeStatusFilter(isAdminPath ? 'pending_review' : 'all');
    setRechargeCurrencyFilter('all');
    setRechargeCustomerFilter('all');
    setRechargePage(1);
    setTransactionCustomerFilter('all');
    setTransactionCurrencyFilter('all');
    setTransactionPage(1);
    setExchangeCustomerId(isAdminPath ? null : (Number(user?.id || 0) || null));
    setExchangeSourceAccountId(null);
    setExchangeTargetAccountId(null);
  }, [isAdminPath]);

  useEffect(() => {
    if (!isAdminPath) return;
    setAccountPage(1);
    setAdminCurrencyPages({});
    setAdminCurrencyFilter('all');
  }, [isAdminPath, manualCustomerId]);

  const handleRechargeStatusFilterChange = useCallback((value: string) => {
    setRechargeStatusFilter(value);
    setRechargePage(1);
  }, []);

  const handleRechargeCurrencyFilterChange = useCallback((value: string) => {
    setRechargeCurrencyFilter(value);
    setRechargePage(1);
  }, []);

  const handleRechargeCustomerFilterChange = useCallback((value: string | number) => {
    setRechargeCustomerFilter(value);
    setRechargePage(1);
  }, []);

  const handleRechargePageChange = useCallback((page: number, pageSize: number) => {
    setRechargePage(page);
    setRechargePageSize(pageSize);
  }, []);

  const handleTransactionPageChange = useCallback((page: number, pageSize: number) => {
    setTransactionPage(page);
    setTransactionPageSize(pageSize);
  }, []);

  const handleTransactionCustomerFilterChange = useCallback((value: string | number) => {
    setTransactionCustomerFilter(value);
    setTransactionPage(1);
  }, []);

  const handleTransactionCurrencyFilterChange = useCallback((value: string) => {
    setTransactionCurrencyFilter(value);
    setTransactionPage(1);
  }, []);

  const handleTransactionDirectionFilterChange = useCallback((value: string) => {
    setTransactionDirectionFilter(value);
    setTransactionPage(1);
  }, []);

  const handleTransactionTypeFilterChange = useCallback((value: string) => {
    setTransactionTypeFilter(value);
    setTransactionPage(1);
  }, []);

  const handleTransactionDateRangeChange = useCallback((vals: [Dayjs | null, Dayjs | null] | null) => {
    setTransactionDateRange(vals);
    setTransactionPage(1);
  }, []);

  const resetTransactionFilters = useCallback(() => {
    setTransactionCustomerFilter('all');
    setTransactionCurrencyFilter('all');
    setTransactionDirectionFilter('all');
    setTransactionTypeFilter('all');
    setTransactionFuzzyFilter('');
    setTransactionDateRange(null);
    setTransactionPage(1);
  }, []);

  const switchTabWithUrl = useCallback((key: string) => {
    setActiveTabKey(key);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (key === 'accounts') {
        url.searchParams.delete('tab');
      } else {
        url.searchParams.set('tab', key);
      }
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  }, []);

  const jumpToTransactionsWithFilters = useCallback((opts?: { customerId?: number | null; currency?: string }) => {
    const customerId = Number(opts?.customerId || 0);
    const currency = String(opts?.currency || '').trim().toUpperCase();
    if (isAdminPath) {
      setTransactionCustomerFilter(customerId > 0 ? customerId : 'all');
    }
    setTransactionCurrencyFilter(currency || 'all');
    setTransactionDirectionFilter('all');
    setTransactionTypeFilter('all');
    setTransactionDateRange(null);
    setTransactionPage(1);
    switchTabWithUrl('transactions');
  }, [isAdminPath, switchTabWithUrl]);

  const renderTransactionRemark = useCallback((row: FundsTransaction) => {
    const raw = String(row.remark || '').trim();
    if (!raw) return '-';
    const matched = raw.match(/^\[([^\]]+)\]\s*(.*)$/);
    let displayText = raw;
    if (matched) {
      const reasonCode = String(matched[1] || '').trim();
      const reasonLabel = MANUAL_REASON_LABEL[reasonCode] || reasonCode;
      const remain = String(matched[2] || '').trim();
      displayText = `[${reasonLabel}]${remain ? ` ${remain}` : ''}`;
    }
    return (
      <Tooltip title={displayText}>
        <span>{displayText}</span>
      </Tooltip>
    );
  }, []);

  const formatTransactionRemark = useCallback((remark?: string) => {
    const raw = String(remark || '').trim();
    if (!raw) return '';
    const matched = raw.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (!matched) return raw;
    const reasonCode = String(matched[1] || '').trim();
    const reasonLabel = MANUAL_REASON_LABEL[reasonCode] || reasonCode;
    const remain = String(matched[2] || '').trim();
    return `[${reasonLabel}]${remain ? ` ${remain}` : ''}`;
  }, []);

  const downloadTransactionsExcel = useCallback(async () => {
    setTransactionExporting(true);
    try {
      const baseParams = buildTransactionQueryParams();
      const pageSize = 500;
      let page = 1;
      let total = 0;
      let done = false;
      const allRows: FundsLedgerEntry[] = [];

      while (!done) {
        const resp = await fetchFundsLedgerEntriesPaged({
          ...baseParams,
          page,
          page_size: pageSize,
        });
        const rows = resp.results || [];
        total = Number(resp.count || 0);
        allRows.push(...rows);
        if (!rows.length || allRows.length >= total) {
          done = true;
        } else {
          page += 1;
        }
      }

      if (!allRows.length) {
        message.warning('当前筛选条件下没有可下载的记录');
        return;
      }

      const exportRows = mapLedgerToTx(allRows).map((row) => ({
        系统编码流水号: row.reference_no,
        ...(isAdminPath
          ? { 所属客户: customerLabelMap?.[Number(row.customer_id)] || `客户#${row.customer_id}` }
          : {}),
        类型: BIZ_TYPE_LABEL[row.type] || row.type,
        关联单号: row.source_label || '',
        状态: STATUS_LABEL[row.status] || row.status,
        币种: String(row.currency || '').toUpperCase(),
        总余额变动前: Number(row.total_before || 0).toFixed(2),
        总余额变动值: Number(row.total_change || 0).toFixed(2),
        总余额变动后: Number(row.total_after || 0).toFixed(2),
        冻结金额变动前: Number(row.frozen_before || 0).toFixed(2),
        冻结金额变动值: Number(row.frozen_change || 0).toFixed(2),
        冻结金额变动后: Number(row.frozen_after || 0).toFixed(2),
        可用余额变动前: Number(row.balance_before || 0).toFixed(2),
        可用余额变动值: Number(row.amount || 0).toFixed(2),
        可用余额变动后: Number(row.balance_after || 0).toFixed(2),
        备注: formatTransactionRemark(row.remark),
        时间: row.created_at ? new Date(row.created_at).toLocaleString('zh-CN') : '',
      }));

      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '交易记录');
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      XLSX.writeFile(wb, `交易记录_${ts}.xlsx`);
      message.success(`下载成功，共导出 ${exportRows.length} 条记录`);
    } catch (e: any) {
      message.error(e?.message || '下载失败');
    } finally {
      setTransactionExporting(false);
    }
  }, [buildTransactionQueryParams, customerLabelMap, formatTransactionRemark, isAdminPath, mapLedgerToTx]);

  const handleClientAccountPageChange = useCallback((page: number, pageSize: number) => {
    setAccountPage(page);
    setAccountPageSize(pageSize);
  }, []);

  const handleAdminCurrencyPageChange = useCallback((currencyCode: string, page: number, pageSize: number) => {
    setAdminCurrencyPages((prev) => ({
      ...prev,
      [currencyCode]: { page, pageSize },
    }));
  }, []);

  // 冻结这一侧还是"填一个金额"；解冻这一侧改成"勾选记录"，预览的可用/冻结余额变化
  // 分别用两种不同的输入来源计算。
  const selectedUnfreezeTotal = useMemo(
    () => unfreezeCandidates
      .filter((r) => selectedUnfreezeIds.includes(r.id))
      .reduce((sum, r) => sum + Number(r.amount || 0), 0),
    [unfreezeCandidates, selectedUnfreezeIds]
  );

  const freezeControlPreview = useMemo(() => {
    const available = Number(freezeControlAccount?.available_balance || 0);
    const frozen = Number(freezeControlAccount?.frozen_balance || 0);
    if (freezeControlAction === 'freeze') {
      const amount = Number(freezeControlAmount || 0);
      const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : 0;
      return {
        maxAmount: Math.max(available, 0),
        estimatedAvailable: available - safeAmount,
        estimatedFrozen: frozen + safeAmount,
      };
    }
    return {
      maxAmount: Math.max(frozen, 0),
      estimatedAvailable: available + selectedUnfreezeTotal,
      estimatedFrozen: frozen - selectedUnfreezeTotal,
    };
  }, [freezeControlAccount, freezeControlAction, freezeControlAmount, selectedUnfreezeTotal]);

  const loadUnfreezeCandidates = useCallback(async (accountId: number) => {
    setUnfreezeCandidatesLoading(true);
    try {
      const resp = await fetchFundsFreezeRecords({ account: accountId, status: 'frozen', page_size: 200 });
      setUnfreezeCandidates(resp.results || []);
    } catch (e) {
      console.error(e);
      message.error('加载冻结记录失败');
    } finally {
      setUnfreezeCandidatesLoading(false);
    }
  }, []);

  const openFreezeControlModal = useCallback((account: FundsAccount) => {
    const available = Number(account?.available_balance || 0);
    const frozen = Number(account?.frozen_balance || 0);
    const defaultAction: 'freeze' | 'unfreeze' = available > 0 ? 'freeze' : (frozen > 0 ? 'unfreeze' : 'freeze');
    setFreezeControlAccount(account);
    setFreezeControlAction(defaultAction);
    setFreezeControlAmount(null);
    setFreezeControlRemark('');
    setSelectedUnfreezeIds([]);
    setUnfreezeCandidates([]);
    setFreezeControlOpen(true);
    if (defaultAction === 'unfreeze') {
      loadUnfreezeCandidates(account.id);
    }
  }, [loadUnfreezeCandidates]);

  const submitFreezeControl = useCallback(async () => {
    if (!freezeControlAccount) return;
    const remark = String(freezeControlRemark || '').trim();
    if (!remark) {
      message.warning('请填写备注');
      return;
    }

    if (freezeControlAction === 'unfreeze') {
      if (selectedUnfreezeIds.length === 0) {
        message.warning('请至少选择一条要解冻的记录');
        return;
      }
      setFreezeControlSubmitting(true);
      try {
        const result = await executeFundsUnfreeze(freezeControlAccount.id, {
          record_ids: selectedUnfreezeIds,
          remark,
        });
        if (result.failed && result.failed.length > 0) {
          message.warning(`部分记录解冻失败（${result.failed.length} 条），其余已成功`);
        } else {
          message.success('解冻成功');
        }
        setFreezeControlOpen(false);
        setFreezeControlAccount(null);
        setFreezeControlRemark('');
        setSelectedUnfreezeIds([]);
        await refreshAllTabsData();
      } catch (e: any) {
        message.error(e?.response?.data?.detail || e?.message || '操作失败');
      } finally {
        setFreezeControlSubmitting(false);
      }
      return;
    }

    const amount = Number(freezeControlAmount || 0);
    const maxAmount = Number(freezeControlPreview.maxAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      message.warning('请输入有效金额');
      return;
    }
    if (amount > maxAmount + 1e-9) {
      message.warning('冻结金额不能超过可用余额');
      return;
    }
    setFreezeControlSubmitting(true);
    try {
      await executeFundsFreeze(freezeControlAccount.id, { amount, remark });
      message.success('冻结成功');
      setFreezeControlOpen(false);
      setFreezeControlAccount(null);
      setFreezeControlAmount(null);
      setFreezeControlRemark('');
      await refreshAllTabsData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '操作失败');
    } finally {
      setFreezeControlSubmitting(false);
    }
  }, [
    freezeControlAccount,
    freezeControlAction,
    freezeControlAmount,
    freezeControlPreview.maxAmount,
    freezeControlRemark,
    selectedUnfreezeIds,
    refreshAllTabsData,
  ]);

  const loadFreezeDetailPage = useCallback(async (accountId: number, page: number, pageSize: number) => {
    setFreezeDetailLoading(true);
    try {
      const resp = await fetchFundsFreezeRecords({ account: accountId, status: 'frozen', page, page_size: pageSize });
      setFreezeDetailRecords(resp.results || []);
      setFreezeDetailTotal(resp.count || 0);
    } catch (e) {
      console.error(e);
      message.error('加载冻结明细失败');
    } finally {
      setFreezeDetailLoading(false);
    }
  }, []);

  const openFreezeDetailModal = useCallback((account: FundsAccount) => {
    setFreezeDetailAccount(account);
    setFreezeDetailOpen(true);
    setFreezeDetailPage(1);
    loadFreezeDetailPage(account.id, 1, freezeDetailPageSize);
  }, [loadFreezeDetailPage, freezeDetailPageSize]);

  const handleFreezeDetailPageChange = useCallback((page: number, pageSize: number) => {
    setFreezeDetailPage(page);
    setFreezeDetailPageSize(pageSize);
    if (freezeDetailAccount) {
      loadFreezeDetailPage(freezeDetailAccount.id, page, pageSize);
    }
  }, [freezeDetailAccount, loadFreezeDetailPage]);

  const handleDownloadFreezeDetail = useCallback(async () => {
    if (!freezeDetailAccount) return;
    setFreezeDetailDownloading(true);
    try {
      let page = 1;
      const pageSize = 200;
      let total = Infinity;
      const allRows: FundsFreezeRecord[] = [];
      while (allRows.length < total) {
        const resp = await fetchFundsFreezeRecords({ account: freezeDetailAccount.id, status: 'frozen', page, page_size: pageSize });
        const rows = resp.results || [];
        total = Number(resp.count || 0);
        allRows.push(...rows);
        if (!rows.length) break;
        page += 1;
      }

      if (!allRows.length) {
        message.warning('当前没有可下载的冻结记录');
        return;
      }

      const exportRows = allRows.map((r) => ({
        金额: Number(r.amount || 0).toFixed(2),
        来源: FREEZE_SOURCE_LABEL[r.source_type] || r.source_type,
        关联单号: r.source_label || '',
        冻结原因: r.remark || '',
        冻结时间: r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : '',
        预计自动解冻时间: r.auto_release_at ? new Date(r.auto_release_at).toLocaleString('zh-CN') : '无固定期限',
      }));

      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '冻结明细');
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      XLSX.writeFile(wb, `冻结明细_${ts}.xlsx`);
      message.success(`下载成功，共导出 ${exportRows.length} 条记录`);
    } catch (e: any) {
      message.error(e?.message || '下载失败');
    } finally {
      setFreezeDetailDownloading(false);
    }
  }, [freezeDetailAccount]);

  const visibleAdminCurrencyList = useMemo(() => {
    if (!isAdminPath) return [];
    if (adminCurrencyFilter === 'all') return adminCurrencyList;
    return adminCurrencyList.filter((c) => c === adminCurrencyFilter);
  }, [adminCurrencyFilter, adminCurrencyList, isAdminPath]);

  const rechargeCurrencyOptions = useMemo(() => {
    const set = new Set<string>();
    allVisibleAccounts.forEach((a) => {
      const c = String(a.currency || '').toUpperCase();
      if (c) set.add(c);
    });
    ['USD', 'CNY', 'EUR'].forEach((c) => set.add(c));
    return Array.from(set).sort();
  }, [allVisibleAccounts]);

  const transactionCurrencyOptions = useMemo(() => {
    const set = new Set<string>();
    adminCurrencyList.forEach((c) => set.add(String(c || '').toUpperCase()));
    allVisibleAccounts.forEach((a) => {
      const code = String(a.currency || '').toUpperCase();
      if (code) set.add(code);
    });
    ['USD', 'CNY', 'EUR'].forEach((c) => set.add(c));
    return Array.from(set).filter(Boolean).sort();
  }, [adminCurrencyList, allVisibleAccounts]);

  const transactionTypeOptions = useMemo(
    () => Object.entries(BIZ_TYPE_LABEL).map(([value, label]) => ({ value, label })),
    []
  );

  useEffect(() => {
    loadExchangeAccounts().catch(() => undefined);
  }, [loadExchangeAccounts]);

  const loadManualLedgerAccounts = useCallback(async (customerId: number | null) => {
    if (!customerId) {
      setManualLedgerAccounts([]);
      return;
    }
    const resp = await fetchFundsAccountsPaged({
      customer_id: Number(customerId),
      page: 1,
      page_size: 300,
    });
    const rows = resp.results || [];
    setManualLedgerAccounts(rows);
    const hasCurrentCurrency = rows.some((r) => String(r.currency || '').toUpperCase() === String(manualLedgerCurrency || '').toUpperCase());
    if (!hasCurrentCurrency && rows[0]?.currency) {
      setManualLedgerCurrency(String(rows[0].currency || '').toUpperCase());
    }
  }, [manualLedgerCurrency]);

  const openManualLedgerModal = useCallback(() => {
    const defaultCustomer = isAdminPath
      ? (Number(transactionCustomerFilter !== 'all' ? transactionCustomerFilter : manualCustomerId || 0) || null)
      : (Number(user?.id || 0) || null);
    setManualLedgerCustomerId(defaultCustomer);
    setManualLedgerDirection('credit');
    setManualLedgerAmount(null);
    setManualLedgerRemark('');
    setManualLedgerReasonType('manual_adjustment');
    setManualLedgerCurrency('USD');
    setManualLedgerConfirmOpen(false);
    setManualLedgerOpen(true);
    loadManualLedgerAccounts(defaultCustomer).catch(() => undefined);
  }, [isAdminPath, loadManualLedgerAccounts, manualCustomerId, transactionCustomerFilter, user?.id]);

  const selectedManualLedgerAccount = useMemo(
    () => manualLedgerAccounts.find((a) => String(a.currency || '').toUpperCase() === String(manualLedgerCurrency || '').toUpperCase()) || null,
    [manualLedgerAccounts, manualLedgerCurrency]
  );

  const manualLedgerCurrencyOptions = useMemo(() => {
    const set = new Set<string>(['USD', 'CNY', 'EUR']);
    manualLedgerAccounts.forEach((a) => {
      const c = String(a.currency || '').toUpperCase();
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [manualLedgerAccounts]);

  const manualLedgerPreview = useMemo(() => {
    const matchedByCurrency =
      manualLedgerAccounts.find((a) => String(a.currency || '').toUpperCase() === String(manualLedgerCurrency || '').toUpperCase()) || null;
    const previewAccount = selectedManualLedgerAccount || matchedByCurrency;
    const before = Number(previewAccount?.available_balance || 0);
    const amount = Number(manualLedgerAmount || 0);
    const delta = manualLedgerDirection === 'credit' ? amount : -amount;
    const after = before + delta;
    return {
      before,
      amount,
      delta,
      after,
      currency: String((previewAccount?.currency || manualLedgerCurrency || '')).toUpperCase(),
    };
  }, [manualLedgerAccounts, manualLedgerAmount, manualLedgerCurrency, manualLedgerDirection, selectedManualLedgerAccount]);

  const validateManualLedgerPayload = useCallback(() => {
    if (!manualLedgerCustomerId || !manualLedgerCurrency) {
      message.warning('请先选择客户和币种');
      return false;
    }
    const amt = Number(manualLedgerAmount || 0);
    if (!Number.isFinite(amt) || amt <= 0) {
      message.warning('请输入有效金额');
      return false;
    }
    if (!manualLedgerRemark.trim()) {
      message.warning('请填写备注');
      return false;
    }
    return true;
  }, [manualLedgerAmount, manualLedgerCurrency, manualLedgerCustomerId, manualLedgerRemark]);

  const openManualLedgerConfirm = useCallback(() => {
    if (!validateManualLedgerPayload()) return;
    setManualLedgerConfirmOpen(true);
  }, [validateManualLedgerPayload]);

  const submitManualLedger = useCallback(async () => {
    if (!validateManualLedgerPayload()) return;
    const customerId = Number(manualLedgerCustomerId || 0);
    const amt = Number(manualLedgerAmount || 0);
    if (!customerId || !Number.isFinite(amt) || amt <= 0) return;
    setManualLedgerSubmitting(true);
    try {
      await createManualFundsLedgerEntry({
        customer_id: customerId,
        currency: manualLedgerCurrency,
        direction: manualLedgerDirection,
        amount: Number(amt.toFixed(2)),
        reason_type: manualLedgerReasonType,
        remark: manualLedgerRemark.trim(),
      });
      message.success('手动交易记录创建成功');
      setManualLedgerConfirmOpen(false);
      setManualLedgerOpen(false);
      await refreshAllTabsData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '手动创建失败');
    } finally {
      setManualLedgerSubmitting(false);
    }
  }, [
    manualLedgerAmount,
    manualLedgerCurrency,
    manualLedgerCustomerId,
    manualLedgerDirection,
    manualLedgerReasonType,
    manualLedgerRemark,
    refreshAllTabsData,
    validateManualLedgerPayload,
  ]);

  const openApproveModal = (row: RechargeOrder) => {
    setApproveTarget(row);
    setApproveAmount(Number(row.requested_amount || 0));
    setApproveOpen(true);
  };

  const confirmApproveRecharge = async () => {
    if (!approveTarget) return;
    const amt = Number(approveAmount || 0);
    if (!Number.isFinite(amt) || amt <= 0) {
      message.warning('请输入有效的实际到账金额');
      return;
    }
    setApproveSubmitting(true);
    try {
      await approveRechargeOrder(approveTarget.id, amt);
      message.success('已审核通过并入账');
      setApproveOpen(false);
      setApproveTarget(null);
      setApproveAmount(null);
      await refreshAllTabsData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '审核通过失败');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const openRejectModal = (row: RechargeOrder) => {
    setRejectTarget(row);
    setRejectReason('');
    setRejectOpen(true);
  };

  const confirmRejectRecharge = async () => {
    if (!rejectTarget) return;
    const reason = String(rejectReason || '').trim();
    if (!reason) {
      message.warning('请填写驳回原因');
      return;
    }
    setRejectSubmitting(true);
    try {
      await rejectRechargeOrder(rejectTarget.id, reason);
      message.success('已驳回该申报');
      setRejectOpen(false);
      setRejectTarget(null);
      setRejectReason('');
      await refreshAllTabsData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '驳回失败');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const openExchangeModal = async () => {
    await loadExchangeAccounts();
    const preferred = exchangeTargetCandidates[0] || null;
    setExchangeTargetAccountId(preferred ? preferred.id : null);
    setExchangeFromAmount(null);
    setExchangeRate(null);
    setExchangeRateSource('manual');
    setSelectedFxSnapshotId(null);
    setExchangeOpen(true);
    fetchFxRateSnapshots({ page_size: 100 }).then(setFxSnapshots).catch(() => undefined);
  };

  useEffect(() => {
    if (!exchangeTargetCandidates.length) {
      setExchangeTargetAccountId(null);
      return;
    }
    if (!exchangeTargetAccountId || !exchangeTargetCandidates.some((x) => x.id === exchangeTargetAccountId)) {
      setExchangeTargetAccountId(exchangeTargetCandidates[0].id);
    }
  }, [exchangeTargetCandidates, exchangeTargetAccountId]);

  const doExchange = async () => {
    if (!selectedExchangeSourceAccount) return;
    const fromAmount = Number(exchangeFromAmount || 0);
    const target = exchangeTargetCandidates.find((x) => x.id === exchangeTargetAccountId);
    if (!target) {
      message.warning('请选择目标账户');
      return;
    }
    if (!Number.isFinite(fromAmount) || fromAmount <= 0) {
      message.warning('请输入有效兑换金额');
      return;
    }
    let rate = Number(exchangeRate || 0);
    let rateSnapshotId: number | undefined = undefined;
    if (exchangeRateSource === 'snapshot') {
      const snap = fxCandidates.find((x) => x.id === selectedFxSnapshotId);
      if (!snap) {
        message.warning('请选择有效汇率快照');
        return;
      }
      rate = Number(snap.rate || 0);
      rateSnapshotId = snap.id;
    } else if (!Number.isFinite(rate) || rate <= 0) {
      message.warning('请输入有效汇率');
      return;
    }
    const avail = Number(selectedExchangeSourceAccount.available_balance || 0);
    if (fromAmount > avail + 1e-9) {
      message.warning(`兑换金额不能超过可用余额 ${avail.toFixed(2)} ${selectedExchangeSourceAccount.currency}`);
      return;
    }
    const toEstimated = Number((fromAmount * rate).toFixed(6));
    setExchangeSubmitting(true);
    try {
      const created = await createExchangeOrder({
        customer_id: selectedExchangeSourceAccount.customer_id,
        from_account: selectedExchangeSourceAccount.id,
        to_account: target.id,
        from_currency: selectedExchangeSourceAccount.currency,
        to_currency: target.currency,
        from_amount: fromAmount,
        to_amount_estimated: toEstimated,
        rate_snapshot: rateSnapshotId,
        remark: `manual_rate=${rate}`,
      });
      await executeExchangeOrder(created.id);
      message.success('兑换成功');
      setExchangeOpen(false);
      await refreshAllTabsData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '兑换失败');
    } finally {
      setExchangeSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
        <Typography.Title level={3} style={{ margin: 0 }}>
          {isAdminPath ? '资金账户（管理员视图）' : '资金账户'}
        </Typography.Title>
        <Button onClick={refreshAllTabsData} loading={loading}>
          刷新
        </Button>
      </Space>
      <Tabs
        activeKey={activeTabKey}
        onChange={switchTabWithUrl}
        items={[
          {
            key: 'accounts',
            label: '资金账户列表',
            children: isAdminPath ? (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card>
                  <Space size={10} wrap>
                    <Typography.Text type="secondary">所属客户</Typography.Text>
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      style={{ width: 320 }}
                      value={manualCustomerId ?? undefined}
                      options={adminCustomerOptions}
                      placeholder="全部客户"
                      onChange={(v) => setManualCustomerId(typeof v === 'number' ? v : null)}
                    />
                      <Typography.Text type="secondary">币种</Typography.Text>
                      <Select
                        allowClear
                        style={{ width: 220 }}
                        value={adminCurrencyFilter}
                        options={[
                          { value: 'all', label: '全部币种' },
                          ...adminCurrencyList.map((c) => ({ value: c, label: formatCurrencyWithSymbol(c) })),
                        ]}
                        placeholder="全部币种"
                        onChange={(v) => setAdminCurrencyFilter(String(v || 'all'))}
                      />
                      <Switch
                        checked={adminNegativeOnly}
                        onChange={(checked) => {
                          setAdminNegativeOnly(checked);
                          setAdminCurrencyPages((prev) => {
                            const next: Record<string, { page: number; pageSize: number }> = {};
                            Object.entries(prev).forEach(([cur, p]) => {
                              next[cur] = { page: 1, pageSize: p.pageSize };
                            });
                            return next;
                          });
                        }}
                        checkedChildren="仅负值"
                        unCheckedChildren="全部"
                      />
                      <Typography.Text type="secondary">仅显示负值账户</Typography.Text>
                  </Space>
                </Card>
                {visibleAdminCurrencyList.length === 0 ? (
                  <Alert type="info" showIcon message="当前无资金账户数据,充值后将自动创建资金账户" />
                ) : (
                  visibleAdminCurrencyList.map((cur) => {
                    const list = adminCurrencyRows[cur] || [];
                    const p = adminCurrencyPages[cur] || { page: 1, pageSize: 20 };
                    const summary = adminCurrencySummary[cur] || { customerCount: 0, totalBalance: 0 };
                    return (
                      <Card
                        key={cur}
                        title={(
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 30 }}>
                            <span>{`${formatCurrencyWithSymbol(cur)} 资金账户`}</span>
                            <span style={{ color: '#666', fontSize: 13 }}>
                              {`${summary.customerCount}个客户  总额${summary.totalBalance.toFixed(2)}`}
                            </span>
                          </span>
                        )}
                      >
                        <Table<FundsAccount>
                          rowKey={(r) => String(r.id)}
                          size="small"
                          pagination={{
                            ...buildTablePagination(adminCurrencyTotals[cur] || 0, p.pageSize),
                            current: p.page,
                            onChange: (page, pageSize) => handleAdminCurrencyPageChange(cur, page, pageSize),
                          }}
                          dataSource={list}
                          onRow={(record) => ({
                            onClick: () => setSelectedAccountId(record.id),
                            style: selectedAccount?.id === record.id ? { backgroundColor: '#e6f7ff' } : {},
                          })}
                          columns={[
                            { title: '账户ID', dataIndex: 'id', width: 90 },
                            { title: '客户ID', dataIndex: 'customer_id', width: 120 },
                            {
                              title: '客户名称',
                              dataIndex: 'customer_id',
                              width: 220,
                              render: (v: number) => customerLabelMap?.[Number(v)] || `客户#${v}`,
                            },
                            {
                              title: '当前余额',
                              key: 'total_balance',
                              width: 120,
                              align: 'right',
                              render: (_: any, r) => (Number(r.available_balance || 0) + Number(r.frozen_balance || 0)).toFixed(2),
                            },
                            {
                              title: '冻结金额',
                              dataIndex: 'frozen_balance',
                              width: 150,
                              align: 'right',
                              render: (v: string, r) => (
                                <Space size={4}>
                                  <span>{Number(v || 0).toFixed(2)}</span>
                                  <Typography.Link
                                    style={{ fontSize: 12 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openFreezeDetailModal(r);
                                    }}
                                  >
                                    明细
                                  </Typography.Link>
                                </Space>
                              ),
                            },
                            {
                              title: '可用余额',
                              dataIndex: 'available_balance',
                              width: 120,
                              align: 'right',
                              render: (v: string) => {
                                const amount = Number(v || 0);
                                return (
                                <span style={getAvailableBalancePillStyle(amount)}>
                                  {amount.toFixed(2)}
                                </span>
                                );
                              },
                            },
                            {
                              title: '更新时间',
                              dataIndex: 'updated_at',
                              width: 180,
                              render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
                            },
                            {
                              title: '操作',
                              key: 'actions',
                              width: 200,
                              render: (_: any, r) => (
                                <Space size={10}>
                                  <Button
                                    type="link"
                                    style={{ paddingInline: 0 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      jumpToTransactionsWithFilters({
                                        customerId: Number(r.customer_id || 0),
                                        currency: String(r.currency || '').toUpperCase(),
                                      });
                                    }}
                                  >
                                    查看交易记录
                                  </Button>
                                  <Button
                                    type="link"
                                    style={{ paddingInline: 0 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openFreezeControlModal(r);
                                    }}
                                  >
                                    冻结/解冻
                                  </Button>
                                </Space>
                              ),
                            },
                          ]}
                        />
                      </Card>
                    );
                  })
                )}
              </Space>
            ) : accounts.length === 0 ? (
              <Alert type="info" showIcon message="当前无资金账户数据,充值后将自动创建资金账户" />
            ) : (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space wrap size={16} style={{ width: '100%' }}>
                    {accounts
                      .slice()
                      .sort((a, b) => String(a.currency || '').localeCompare(String(b.currency || '')) || a.id - b.id)
                      .map((acc) => {
                        const total = Number(acc.available_balance || 0) + Number(acc.frozen_balance || 0);
                        const active = selectedAccount?.id === acc.id;
                        return (
                          <Card
                            key={acc.id}
                            title={formatCurrencyWithSymbol(acc.currency)}
                            extra={(
                              <Space size={12}>
                                <Typography.Link
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openFreezeDetailModal(acc);
                                  }}
                                >
                                  冻结明细
                                </Typography.Link>
                                <Typography.Link
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    jumpToTransactionsWithFilters({
                                      currency: String(acc.currency || '').toUpperCase(),
                                    });
                                  }}
                                >
                                  查看交易记录
                                </Typography.Link>
                              </Space>
                            )}
                            style={{
                              width: 320,
                              borderColor: active ? '#91caff' : undefined,
                              boxShadow: active ? '0 0 0 2px rgba(24,144,255,0.12)' : undefined,
                            }}
                            bodyStyle={{ paddingTop: 12, paddingBottom: 12 }}
                            onClick={() => setSelectedAccountId(acc.id)}
                          >
                            <Space size={18} wrap>
                              <Typography.Text>当前余额 {total.toFixed(2)}</Typography.Text>
                              <Typography.Text>冻结金额 {Number(acc.frozen_balance || 0).toFixed(2)}</Typography.Text>
                              <span style={getAvailableBalancePillStyle(Number(acc.available_balance || 0))}>
                                可用余额 {Number(acc.available_balance || 0).toFixed(2)}
                              </span>
                              <Typography.Text type="secondary">
                                更新时间 {acc.updated_at ? new Date(acc.updated_at).toLocaleString('zh-CN') : '-'}
                              </Typography.Text>
                            </Space>
                          </Card>
                        );
                      })}
                  </Space>
                  <Pagination
                    {...buildTablePagination(accountTotal, accountPageSize)}
                    current={accountPage}
                    onChange={handleClientAccountPageChange}
                  />
                </Space>
              ),
          },
          {
            key: 'recharge',
            label: '充值',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card>
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <Space size={20}>
                      <Button onClick={() => message.info('在线充值功能后续开发')} disabled>
                        在线充值（建设中）
                      </Button>
                      <Button type="primary" onClick={() => {
                        setRechargeCurrency('USD');
                        if (isAdminPath) {
                          setRechargeCustomerId(Number(manualCustomerId || 0) || null);
                          searchRechargeCustomers('');
                        }
                        setRechargeOpen(true);
                      }}>
                        线下到账申报
                      </Button>
                    </Space>
                    <Typography.Text type="secondary">
                      线下到账申报用于“客户已通过银行、Zelle 等线下方式向我司账户付款”后的到账申请。提交后进入待审核，管理员核查凭证后确认入账到对应客户币种资金账户。
                    </Typography.Text>
                  </Space>
                </Card>
                {isAdminPath && (
                  <Card title="线下到账申报审核列表">
                    <RechargeOrderTable
                      mode="admin"
                      rows={reviewRows}
                      loading={loading}
                      total={rechargeTotal}
                      current={rechargePage}
                      pageSize={rechargePageSize}
                      statusFilter={rechargeStatusFilter}
                      currencyFilter={rechargeCurrencyFilter}
                      customerFilter={rechargeCustomerFilter}
                      copyText={copyText}
                      parseVoucherFiles={parseVoucherFiles}
                      onPreviewFile={openVoucherPreviewByNameUrl}
                      customerOptions={adminCustomerOptions}
                      customerLabelMap={customerLabelMap}
                      onStatusFilterChange={handleRechargeStatusFilterChange}
                      onCurrencyFilterChange={handleRechargeCurrencyFilterChange}
                      onCustomerFilterChange={handleRechargeCustomerFilterChange}
                      onPageChange={handleRechargePageChange}
                      currencyOptions={rechargeCurrencyOptions}
                      onApprove={openApproveModal}
                      onReject={openRejectModal}
                    />
                  </Card>
                )}
                {!isAdminPath && (
                  <Card title="我的线下到账申报记录">
                    <RechargeOrderTable
                      mode="client"
                      rows={clientApplyRows}
                      loading={loading}
                      total={rechargeTotal}
                      current={rechargePage}
                      pageSize={rechargePageSize}
                      statusFilter={rechargeStatusFilter}
                      currencyFilter={rechargeCurrencyFilter}
                      copyText={copyText}
                      parseVoucherFiles={parseVoucherFiles}
                      onPreviewFile={openVoucherPreviewByNameUrl}
                      onStatusFilterChange={handleRechargeStatusFilterChange}
                      onCurrencyFilterChange={handleRechargeCurrencyFilterChange}
                      onPageChange={handleRechargePageChange}
                      currencyOptions={rechargeCurrencyOptions}
                    />
                  </Card>
                )}
              </Space>
            ),
          },
          {
            key: 'exchange',
            label: '币种兑换',
            children: (
              <Card
                title="币种兑换"
                extra={
                  <Button onClick={openExchangeModal} type="primary" disabled>
                    发起兑换
                  </Button>
                }
              >
                <Typography.Text type="secondary">
                  将当前选中账户余额兑换到同客户下其他币种账户（当前支持手工输入汇率和汇率快照）。
                </Typography.Text>
              </Card>
            ),
          },
          {
            key: 'transactions',
            label: '交易记录',
            children: (
              <Card
                title="交易记录"
                extra={
                  <Space size={8}>
                    {isAdminPath ? (
                      <Button size="small" type="primary" onClick={openManualLedgerModal}>
                        手动创建记录
                      </Button>
                    ) : null}
                    <Button size="small" onClick={downloadTransactionsExcel} loading={transactionExporting}>
                      下载记录({transactionTotal}条)
                    </Button>
                    {isAdminPath ? (
                      <Button size="small" onClick={() => setTransactionHelpOpen(true)}>
                        说明
                      </Button>
                    ) : null}
                  </Space>
                }
              >
                <Space size={10} wrap style={{ marginBottom: 12 }}>
                  {isAdminPath ? (
                    <>
                      <Typography.Text type="secondary">所属客户</Typography.Text>
                      <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        style={{ width: 260 }}
                        value={transactionCustomerFilter}
                        options={[
                          { value: 'all', label: '全部客户' },
                          ...adminCustomerOptions,
                        ]}
                        placeholder="全部客户"
                        onChange={(v) => handleTransactionCustomerFilterChange(v ?? 'all')}
                      />
                    </>
                  ) : null}
                  <Typography.Text type="secondary">币种</Typography.Text>
                  <Select
                    allowClear
                    style={{ width: 180 }}
                    value={transactionCurrencyFilter}
                    options={[
                      { value: 'all', label: '全部币种' },
                      ...transactionCurrencyOptions.map((c) => ({ value: c, label: formatCurrencyWithSymbol(c) })),
                    ]}
                    placeholder="全部币种"
                    onChange={(v) => handleTransactionCurrencyFilterChange(String(v ?? 'all'))}
                  />
                  <Typography.Text type="secondary">交易方向</Typography.Text>
                  <Select
                    allowClear
                    style={{ width: 160 }}
                    value={transactionDirectionFilter}
                    options={[
                      { value: 'all', label: '全部方向' },
                      { value: 'credit', label: '增加' },
                      { value: 'debit', label: '减少' },
                    ]}
                    placeholder="全部方向"
                    onChange={(v) => handleTransactionDirectionFilterChange(String(v ?? 'all'))}
                  />
                  <Typography.Text type="secondary">类型</Typography.Text>
                  <Select
                    allowClear
                    style={{ width: 180 }}
                    value={transactionTypeFilter}
                    options={[{ value: 'all', label: '全部类型' }, ...transactionTypeOptions]}
                    placeholder="全部类型"
                    onChange={(v) => handleTransactionTypeFilterChange(String(v ?? 'all'))}
                  />
                  <Typography.Text type="secondary">模糊匹配</Typography.Text>
                  <Input
                    allowClear
                    style={{ width: 240 }}
                    value={transactionFuzzyFilter}
                    placeholder="按系统编码/流水号/关联单号/备注模糊搜索"
                    onChange={(e) => {
                      setTransactionFuzzyFilter(e.target.value);
                      setTransactionPage(1);
                    }}
                  />
                  <Typography.Text type="secondary">时间区间</Typography.Text>
                  <DatePicker.RangePicker
                    value={transactionDateRange as any}
                    onChange={(vals) => handleTransactionDateRangeChange((vals as any) || null)}
                    allowClear
                  />
                  <Button
                    type="primary"
                    style={{ marginLeft: 10 }}
                    onClick={() => {
                      setTransactionPage(1);
                      setTransactionSearchTick((x) => x + 1);
                    }}
                  >
                    搜索
                  </Button>
                  <Button
                    onClick={() => {
                      resetTransactionFilters();
                      setTransactionSearchTick((x) => x + 1);
                    }}
                  >
                    重置
                  </Button>
                </Space>
                <Table<FundsTransaction>
                  rowKey="id"
                  dataSource={transactions}
                  loading={loading}
                  scroll={{ x: 2060 }}
                  pagination={{
                    ...buildTablePagination(transactionTotal, transactionPageSize),
                    current: transactionPage,
                    onChange: handleTransactionPageChange,
                  }}
                  columns={[
                    {
                      title: '系统编码/流水号',
                      dataIndex: 'reference_no',
                      width: 240,
                      render: (v: string) => (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Typography.Text code>{v}</Typography.Text>
                          <CopyOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => copyText(v)} />
                        </span>
                      ),
                    },
                    ...(isAdminPath
                      ? [{
                          title: '所属客户',
                          dataIndex: 'customer_id',
                          width: 220,
                          render: (v: number) => customerLabelMap?.[Number(v)] || `客户#${v}`,
                        } as any]
                      : []),
                    { title: '类型', dataIndex: 'type', width: 120, render: (v: string) => BIZ_TYPE_LABEL[v] || v },
                    {
                      title: '关联单号',
                      dataIndex: 'source_label',
                      width: 160,
                      render: (v: string) =>
                        v ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Typography.Text>{v}</Typography.Text>
                            <CopyOutlined style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => copyText(v)} />
                          </span>
                        ) : (
                          '-'
                        ),
                    },
                    { title: '币种', dataIndex: 'currency', width: 110, render: (v: string) => formatCurrencyWithSymbol(v) },
                    {
                      title: '总余额变动',
                      key: 'total_group',
                      children: [
                        {
                          title: '变动前',
                          dataIndex: 'total_before',
                          key: 'total_before',
                          width: 110,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#f0f5ff' } }),
                          onHeaderCell: () => ({ style: { background: '#f0f5ff' } }),
                          render: (v: number) => Number(v || 0).toFixed(2),
                        },
                        {
                          title: '变动值',
                          dataIndex: 'total_change',
                          key: 'total_change',
                          width: 100,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#f0f5ff' } }),
                          onHeaderCell: () => ({ style: { background: '#f0f5ff' } }),
                          render: (v: number) => renderTxDelta(v),
                        },
                        {
                          title: '变动后',
                          dataIndex: 'total_after',
                          key: 'total_after',
                          width: 110,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#f0f5ff' } }),
                          onHeaderCell: () => ({ style: { background: '#f0f5ff' } }),
                          render: (v: number) => Number(v || 0).toFixed(2),
                        },
                      ],
                    },
                    {
                      title: '冻结金额变动',
                      key: 'frozen_group',
                      children: [
                        {
                          title: '变动前',
                          dataIndex: 'frozen_before',
                          key: 'frozen_before',
                          width: 110,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#fff7e6' } }),
                          onHeaderCell: () => ({ style: { background: '#fff7e6' } }),
                          render: (v: number) => Number(v || 0).toFixed(2),
                        },
                        {
                          title: '变动值',
                          dataIndex: 'frozen_change',
                          key: 'frozen_change',
                          width: 100,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#fff7e6' } }),
                          onHeaderCell: () => ({ style: { background: '#fff7e6' } }),
                          render: (v: number) => renderTxDelta(v),
                        },
                        {
                          title: '变动后',
                          dataIndex: 'frozen_after',
                          key: 'frozen_after',
                          width: 110,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#fff7e6' } }),
                          onHeaderCell: () => ({ style: { background: '#fff7e6' } }),
                          render: (v: number) => Number(v || 0).toFixed(2),
                        },
                      ],
                    },
                    {
                      title: '可用余额变动',
                      key: 'available_group',
                      children: [
                        {
                          title: '变动前',
                          dataIndex: 'balance_before',
                          key: 'available_before',
                          width: 110,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#f6ffed' } }),
                          onHeaderCell: () => ({ style: { background: '#f6ffed' } }),
                          render: (v: number) => Number(v || 0).toFixed(2),
                        },
                        {
                          title: '变动值',
                          dataIndex: 'amount',
                          key: 'available_change',
                          width: 100,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#f6ffed' } }),
                          onHeaderCell: () => ({ style: { background: '#f6ffed' } }),
                          render: (v: number) => renderTxDelta(v),
                        },
                        {
                          title: '变动后',
                          dataIndex: 'balance_after',
                          key: 'available_after',
                          width: 110,
                          align: 'right' as const,
                          onCell: () => ({ style: { background: '#f6ffed' } }),
                          onHeaderCell: () => ({ style: { background: '#f6ffed' } }),
                          render: (v: number) => Number(v || 0).toFixed(2),
                        },
                      ],
                    },
                    { title: '备注', dataIndex: 'remark', ellipsis: true, render: (_: string | undefined, row: FundsTransaction) => renderTransactionRemark(row) },
                    { title: '时间', dataIndex: 'created_at', width: 180, render: (v) => new Date(v).toLocaleString('zh-CN') },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        open={freezeControlOpen}
        title="资金冻结/解冻"
        width={freezeControlAction === 'unfreeze' ? 1020 : 760}
        okText="确认执行"
        cancelText="取消"
        confirmLoading={freezeControlSubmitting}
        styles={{ body: { paddingBottom: 28 } }}
        onCancel={() => {
          if (freezeControlSubmitting) return;
          setFreezeControlOpen(false);
          setFreezeControlAccount(null);
          setFreezeControlAmount(null);
          setFreezeControlRemark('');
          setSelectedUnfreezeIds([]);
        }}
        onOk={submitFreezeControl}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap size={20}>
            <Typography.Text>
              所属客户：{customerLabelMap?.[Number(freezeControlAccount?.customer_id || 0)] || '-'}
            </Typography.Text>
            <Typography.Text>
              币种：{formatCurrencyWithSymbol(freezeControlAccount?.currency)}
            </Typography.Text>
          </Space>
          <Space wrap size={20}>
            <Typography.Text>
              当前余额：
              {(Number(freezeControlAccount?.available_balance || 0) + Number(freezeControlAccount?.frozen_balance || 0)).toFixed(2)}
            </Typography.Text>
            <Typography.Text>
              冻结金额：{Number(freezeControlAccount?.frozen_balance || 0).toFixed(2)}
            </Typography.Text>
            <span style={getAvailableBalancePillStyle(Number(freezeControlAccount?.available_balance || 0))}>
              可用余额 {Number(freezeControlAccount?.available_balance || 0).toFixed(2)}
            </span>
          </Space>
          <Space size={12} style={{ width: '100%' }}>
            <Typography.Text type="secondary">本次动作</Typography.Text>
            <Select
              style={{ width: 140 }}
              value={freezeControlAction}
              options={[
                { value: 'freeze', label: '冻结' },
                {
                  value: 'unfreeze',
                  label: '解冻',
                  disabled: Number(freezeControlAccount?.frozen_balance || 0) <= 0,
                },
              ]}
              onChange={(v) => {
                const next = String(v) === 'unfreeze' ? 'unfreeze' : 'freeze';
                setFreezeControlAction(next);
                setFreezeControlAmount(null);
                setSelectedUnfreezeIds([]);
                if (next === 'unfreeze' && freezeControlAccount) {
                  loadUnfreezeCandidates(freezeControlAccount.id);
                }
              }}
            />
            {freezeControlAction === 'freeze' && (
              <>
                <Typography.Text type="secondary">金额</Typography.Text>
                <InputNumber
                  min={0}
                  max={Number(freezeControlPreview.maxAmount || 0)}
                  precision={2}
                  step={0.01}
                  style={{ width: 180 }}
                  value={freezeControlAmount as any}
                  onChange={(v) => {
                    if (v === null) {
                      setFreezeControlAmount(null);
                      return;
                    }
                    const maxAmount = Number(freezeControlPreview.maxAmount || 0);
                    const next = Number(v);
                    if (!Number.isFinite(next)) {
                      setFreezeControlAmount(null);
                      return;
                    }
                    setFreezeControlAmount(Math.min(next, maxAmount));
                  }}
                  placeholder="输入冻结金额"
                />
                <Button
                  size="small"
                  onClick={() => setFreezeControlAmount(Number(freezeControlPreview.maxAmount || 0))}
                  disabled={Number(freezeControlPreview.maxAmount || 0) <= 0}
                >
                  全部金额
                </Button>
              </>
            )}
          </Space>

          {freezeControlAction === 'unfreeze' && (
            <div>
              <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text type="secondary">
                  勾选要解冻的记录（只能整条解冻，不支持部分解冻一条记录）
                </Typography.Text>
                <Checkbox
                  checked={unfreezeCandidates.length > 0 && selectedUnfreezeIds.length === unfreezeCandidates.length}
                  indeterminate={selectedUnfreezeIds.length > 0 && selectedUnfreezeIds.length < unfreezeCandidates.length}
                  onChange={(e) => setSelectedUnfreezeIds(e.target.checked ? unfreezeCandidates.map((r) => r.id) : [])}
                >
                  全选
                </Checkbox>
              </Space>
              <Table<FundsFreezeRecord>
                rowKey="id"
                size="small"
                loading={unfreezeCandidatesLoading}
                dataSource={unfreezeCandidates}
                pagination={false}
                scroll={{ x: 940, y: 260 }}
                locale={{ emptyText: <Empty description="当前没有可解冻的记录" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                rowSelection={{
                  selectedRowKeys: selectedUnfreezeIds,
                  onChange: (keys) => setSelectedUnfreezeIds(keys as number[]),
                }}
                columns={[
                  {
                    title: '金额',
                    dataIndex: 'amount',
                    width: 100,
                    align: 'right',
                    render: (v: string) => Number(v || 0).toFixed(2),
                  },
                  {
                    title: '来源',
                    dataIndex: 'source_type',
                    width: 140,
                    render: (v: string) => FREEZE_SOURCE_LABEL[v] || v,
                  },
                  {
                    title: '关联单号',
                    dataIndex: 'source_label',
                    width: 140,
                    render: (v: string) => v || '-',
                  },
                  {
                    title: '冻结原因',
                    dataIndex: 'remark',
                    width: 200,
                    ellipsis: true,
                    render: (v: string) => v || '-',
                  },
                  {
                    title: '冻结时间',
                    dataIndex: 'created_at',
                    width: 160,
                    render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
                  },
                  {
                    title: '预计自动解冻时间',
                    dataIndex: 'auto_release_at',
                    width: 160,
                    render: (v: string | null) => (v ? new Date(v).toLocaleString('zh-CN') : '无固定期限，需人工解冻'),
                  },
                ]}
              />
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <Typography.Text strong>已选 {selectedUnfreezeIds.length} 条，合计 {selectedUnfreezeTotal.toFixed(2)}</Typography.Text>
              </div>
            </div>
          )}

          <Space wrap size={20}>
            <Typography.Text>
              执行后冻结金额：{Number(freezeControlPreview.estimatedFrozen || 0).toFixed(2)}
            </Typography.Text>
            <span style={getAvailableBalancePillStyle(Number(freezeControlPreview.estimatedAvailable || 0))}>
              执行后可用余额 {Number(freezeControlPreview.estimatedAvailable || 0).toFixed(2)}
            </span>
          </Space>
          <Input.TextArea
            rows={3}
            value={freezeControlRemark}
            onChange={(e) => setFreezeControlRemark(e.target.value)}
            placeholder="请填写操作备注（必填）"
            maxLength={200}
            showCount
          />
        </Space>
      </Modal>

      <Modal
        open={freezeDetailOpen}
        title="冻结明细"
        width={1200}
        footer={null}
        onCancel={() => {
          setFreezeDetailOpen(false);
          setFreezeDetailAccount(null);
          setFreezeDetailRecords([]);
          setFreezeDetailTotal(0);
          setFreezeDetailPage(1);
        }}
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
          <Space wrap size={20}>
            {isAdminPath && (
              <Typography.Text>
                所属客户：{customerLabelMap?.[Number(freezeDetailAccount?.customer_id || 0)] || '-'}
              </Typography.Text>
            )}
            <Typography.Text>
              币种：{formatCurrencyWithSymbol(freezeDetailAccount?.currency)}
            </Typography.Text>
            <Typography.Text>
              当前冻结总额：{Number(freezeDetailAccount?.frozen_balance || 0).toFixed(2)}
            </Typography.Text>
          </Space>
          <Button
            icon={<DownloadOutlined />}
            loading={freezeDetailDownloading}
            onClick={handleDownloadFreezeDetail}
            disabled={freezeDetailTotal === 0}
          >
            下载完整数据
          </Button>
        </Space>
        <Table<FundsFreezeRecord>
          rowKey="id"
          size="small"
          loading={freezeDetailLoading}
          dataSource={freezeDetailRecords}
          scroll={{ x: 1150 }}
          pagination={{
            current: freezeDetailPage,
            pageSize: freezeDetailPageSize,
            total: freezeDetailTotal,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: handleFreezeDetailPageChange,
            showTotal: (total, range) => `显示 ${range[0]}-${range[1]} / 共 ${total} 条`,
          }}
          locale={{ emptyText: <Empty description="暂无冻结记录" /> }}
          columns={[
            {
              title: '金额',
              dataIndex: 'amount',
              width: 100,
              align: 'right',
              render: (v: string) => Number(v || 0).toFixed(2),
            },
            {
              title: '来源',
              dataIndex: 'source_type',
              width: 140,
              render: (v: string) => FREEZE_SOURCE_LABEL[v] || v,
            },
            {
              title: '关联单号',
              dataIndex: 'source_label',
              width: 160,
              render: (v: string) => v || '-',
            },
            {
              title: '冻结原因',
              dataIndex: 'remark',
              width: 220,
              ellipsis: { showTitle: false },
              render: (v: string) => (
                <Tooltip title={v || '-'}>
                  <span>{v || '-'}</span>
                </Tooltip>
              ),
            },
            {
              title: '冻结时间',
              dataIndex: 'created_at',
              width: 160,
              render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
            },
            {
              title: '预计自动解冻时间',
              dataIndex: 'auto_release_at',
              width: 160,
              render: (v: string | null) => (v ? new Date(v).toLocaleString('zh-CN') : '无固定期限'),
            },
          ]}
        />
      </Modal>

      <Modal
        open={rechargeOpen}
        title="线下到账申报"
        okText="提交申报"
        cancelText="取消"
        onCancel={() => setRechargeOpen(false)}
        onOk={doRecharge}
        confirmLoading={rechargeSubmitting}
        okButtonProps={{ disabled: !canSubmitRecharge }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {isAdminPath && (
            <div>
              <Typography.Text>客户（按客户名/编码搜索）</Typography.Text>
              <Select
                showSearch
                allowClear
                filterOption={false}
                style={{ width: '100%', marginTop: 8 }}
                value={rechargeCustomerId ?? undefined}
                loading={rechargeCustomerLoading}
                options={rechargeCustomerOptions}
                placeholder="请选择客户"
                onFocus={() => searchRechargeCustomers('')}
                onSearch={(v) => searchRechargeCustomers(v)}
                onChange={(v) => setRechargeCustomerId(typeof v === 'number' ? v : null)}
              />
            </div>
          )}
          <div>
            <Typography.Text>付款渠道</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={rechargeProvider}
              onChange={setRechargeProvider}
              options={OFFLINE_PROVIDER_OPTIONS}
            />
          </div>
          <div>
            <Typography.Text>币种</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={rechargeCurrency}
              onChange={setRechargeCurrency}
              options={Array.from(new Set([...allVisibleAccounts.map((a) => String(a.currency || '').toUpperCase()), 'USD', 'CNY', 'EUR'].filter(Boolean))).map((c) => ({
                label: formatCurrencyWithSymbol(c),
                value: c,
              }))}
            />
          </div>
          <div>
            <Typography.Text>付款金额（{formatCurrencyWithSymbol(rechargeCurrency || 'USD')}）</Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0.01}
              precision={2}
              step={10}
              value={rechargeAmount}
              onChange={(v) => setRechargeAmount(typeof v === 'number' ? v : null)}
              placeholder="请输入充值金额"
            />
          </div>
          <div>
            <Typography.Text>付款流水号（可选）</Typography.Text>
            <Input
              style={{ marginTop: 8 }}
              value={rechargeTradeNo}
              onChange={(e) => setRechargeTradeNo(e.target.value)}
              placeholder="请输入转账流水号"
            />
          </div>
          <div>
            <Typography.Text>付款凭证（必填，优先上传付款截图，最多3个）</Typography.Text>
            <Upload
              style={{ marginTop: 8 }}
              fileList={rechargeFileList}
              maxCount={3}
              showUploadList={false}
              beforeUpload={() => false}
              onChange={({ fileList }) => {
                setRechargeFileList(fileList.slice(0, 3));
              }}
            >
              <Button disabled={rechargeFileList.length >= 3}>选择付款凭证文件</Button>
            </Upload>
            {rechargeFileList.length > 0 && (
              <div style={{ marginTop: 10, border: '1px solid #f0f0f0', borderRadius: 6 }}>
                {rechargeFileList.map((f, idx) => (
                  <div
                    key={f.uid || `${f.name}-${idx}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      borderTop: idx === 0 ? 'none' : '1px solid #f5f5f5',
                    }}
                  >
                    <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                      <span title={f.name || `文件${idx + 1}`}>
                        {truncateFileName(f.name || `文件${idx + 1}`, 20)}
                      </span>
                    </span>
                    <Space size={2}>
                      <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => openVoucherPreview(f)} />
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => setRechargeFileList((prev) => prev.filter((x) => x.uid !== f.uid))}
                      />
                    </Space>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <Typography.Text>备注信息</Typography.Text>
            <Input.TextArea
              rows={3}
              style={{ marginTop: 8 }}
              value={rechargeRemark}
              onChange={(e) => setRechargeRemark(e.target.value)}
              placeholder="可填写付款人、付款时间、补充说明等"
            />
          </div>
        </Space>
      </Modal>

      <Modal
        open={rejectOpen}
        title={rejectTarget ? `驳回申报：${rejectTarget.order_no}` : '驳回申报'}
        okText="确认驳回"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        onCancel={() => setRejectOpen(false)}
        onOk={confirmRejectRecharge}
        confirmLoading={rejectSubmitting}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            请填写驳回原因，系统将记录在申报备注中。
          </Typography.Text>
          <Input.TextArea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="请输入驳回原因"
          />
        </Space>
      </Modal>

      <Modal
        open={approveOpen}
        title={approveTarget ? `审核通过并入账：${approveTarget.order_no}` : '审核通过并入账'}
        okText="确认通过"
        cancelText="取消"
        onCancel={() => setApproveOpen(false)}
        onOk={confirmApproveRecharge}
        confirmLoading={approveSubmitting}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            申报金额：{Number(approveTarget?.requested_amount || 0).toFixed(2)} {formatCurrencyWithSymbol(approveTarget?.currency || '')}
          </Typography.Text>
          <div>
            <Typography.Text>实际到账金额</Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0.01}
              precision={2}
              value={approveAmount}
              onChange={(v) => setApproveAmount(typeof v === 'number' ? v : null)}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        open={voucherPreviewOpen}
        title={voucherPreviewTitle || '付款凭证预览'}
        footer={[
          <Button key="close" onClick={closeVoucherPreview}>关闭</Button>,
          <Button key="download" type="primary" onClick={downloadVoucherFile}>下载文件</Button>,
        ]}
        onCancel={closeVoucherPreview}
        width={920}
      >
        {voucherPreviewSrc && voucherPreviewType === 'image' ? (
          <img
            src={voucherPreviewSrc}
            alt={voucherPreviewTitle || '付款凭证'}
            style={{ width: '100%', maxHeight: '72vh', objectFit: 'contain' }}
          />
        ) : voucherPreviewSrc && voucherPreviewType === 'pdf' ? (
          <iframe
            src={voucherPreviewSrc}
            title={voucherPreviewTitle || '付款凭证PDF预览'}
            style={{ width: '100%', height: '72vh', border: 'none' }}
          />
        ) : (
          <Alert type="warning" showIcon message="暂不支持在线预览" />
        )}
      </Modal>

      <Modal
        open={exchangeOpen}
        title="币种兑换"
        okText="确认兑换"
        cancelText="取消"
        onCancel={() => setExchangeOpen(false)}
        onOk={doExchange}
        confirmLoading={exchangeSubmitting}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {isAdminPath ? (
            <div>
              <Typography.Text>所属客户</Typography.Text>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                style={{ width: '100%', marginTop: 8 }}
                value={exchangeCustomerId ?? undefined}
                options={adminCustomerOptions}
                placeholder="请选择客户"
                onChange={(v) => {
                  setExchangeCustomerId(typeof v === 'number' ? v : null);
                  setExchangeSourceAccountId(null);
                  setExchangeTargetAccountId(null);
                }}
              />
            </div>
          ) : null}
          <div>
            <Typography.Text>源账户</Typography.Text>
            <Select
              loading={exchangeAccountsLoading}
              style={{ width: '100%', marginTop: 8 }}
              value={exchangeSourceAccountId ?? undefined}
              onChange={(v) => setExchangeSourceAccountId(Number(v))}
              options={exchangeAccounts.map((a) => ({
                value: a.id,
                label: `客户#${a.customer_id} · ${a.currency} · 可用 ${Number(a.available_balance || 0).toFixed(2)}`,
              }))}
              placeholder="请选择源账户"
            />
          </div>
          <div>
            <Typography.Text>目标账户</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={exchangeTargetAccountId ?? undefined}
              onChange={(v) => setExchangeTargetAccountId(Number(v))}
              options={exchangeTargetCandidates.map((a) => ({
                value: a.id,
                label: `客户#${a.customer_id} · ${a.currency} · 可用 ${Number(a.available_balance || 0).toFixed(2)}`,
              }))}
            />
          </div>
          <div>
            <Typography.Text>源币种金额（{selectedExchangeSourceAccount?.currency || '-'}）</Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0.000001}
              precision={6}
              step={1}
              value={exchangeFromAmount}
              onChange={(v) => setExchangeFromAmount(typeof v === 'number' ? v : null)}
              placeholder="请输入兑换金额"
            />
          </div>
          <div>
            <Typography.Text>汇率来源</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={exchangeRateSource}
              onChange={(v) => setExchangeRateSource(v)}
              options={[
                { label: '手工输入', value: 'manual' },
                { label: '汇率快照', value: 'snapshot' },
              ]}
            />
          </div>
          {exchangeRateSource === 'manual' ? (
            <div>
              <Typography.Text>汇率（目标币种/源币种）</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                min={0.000001}
                precision={8}
                step={0.0001}
                value={exchangeRate}
                onChange={(v) => setExchangeRate(typeof v === 'number' ? v : null)}
                placeholder="请输入汇率"
              />
            </div>
          ) : (
            <div>
              <Typography.Text>选择汇率快照</Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={selectedFxSnapshotId ?? undefined}
                onChange={(v) => {
                  setSelectedFxSnapshotId(Number(v));
                  const s = fxCandidates.find((x) => x.id === Number(v));
                  if (s) setExchangeRate(Number(s.rate || 0));
                }}
                options={fxCandidates.map((x) => ({
                  value: x.id,
                  label: `${x.from_currency}/${x.to_currency} = ${x.rate} · ${new Date(x.captured_at).toLocaleString('zh-CN')}`,
                }))}
                placeholder={fxCandidates.length ? '请选择汇率快照' : '当前币种对暂无快照，请改用手工输入'}
              />
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                最近汇率更新时间：
                {fxCandidates.length ? new Date(fxCandidates[0].captured_at).toLocaleString('zh-CN') : '-'}
              </Typography.Text>
            </div>
          )}
          <Alert
            type="info"
            showIcon
            message={`预计到账：${
              Number.isFinite(Number(exchangeFromAmount || 0)) && Number.isFinite(Number(exchangeRate || 0))
                ? Number((Number(exchangeFromAmount || 0) * Number(exchangeRate || 0)).toFixed(6))
                : 0
            } ${exchangeTargetCandidates.find((x) => x.id === exchangeTargetAccountId)?.currency || ''}`}
          />
        </Space>
      </Modal>

      <Modal
        open={transactionHelpOpen}
        title="交易记录说明"
        footer={[
          <Button key="close" type="primary" onClick={() => setTransactionHelpOpen(false)}>
            我知道了
          </Button>,
        ]}
        onCancel={() => setTransactionHelpOpen(false)}
        width={760}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Typography.Text>
            每条交易记录都会绑定到一个唯一的资金账户，并且该记录的币种与该资金账户的币种保持一致。
          </Typography.Text>
          <Typography.Text>
            交易记录中的“交易金额”表示本次变动金额，“交易前余额/交易后余额”都基于资金账户的可用余额口径；其中交易前余额按系统入账时点的可用余额快照计算展示。
          </Typography.Text>
          <Typography.Text>
            如需核对余额变化，可结合方向与金额验证：交易后余额 = 交易前余额 ± 交易金额（增加为加，减少为减）。
          </Typography.Text>
          <Typography.Text type="secondary">
            例如：同一客户同一币种的线下到账申报审核通过后，会写入一条 recharge 类型交易记录，并更新对应资金账户余额。
          </Typography.Text>
          <Typography.Text type="secondary">
            管理员手动创建记录：选择客户与币种后，填写增加/减少方向、金额与备注；提交后系统会按“客户+币种”自动匹配资金账户（不存在则自动创建），并立即写入交易记录与更新余额。
          </Typography.Text>
        </Space>
      </Modal>

      <Modal
        open={manualLedgerOpen}
        title="手动创建交易记录"
        okText="确认创建"
        cancelText="取消"
        confirmLoading={manualLedgerSubmitting}
        onCancel={() => setManualLedgerOpen(false)}
        onOk={openManualLedgerConfirm}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Typography.Text>客户</Typography.Text>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              style={{ width: '100%', marginTop: 8 }}
              value={manualLedgerCustomerId ?? undefined}
              options={adminCustomerOptions}
              placeholder="请选择客户"
              onChange={(v) => {
                const next = typeof v === 'number' ? v : null;
                setManualLedgerCustomerId(next);
                loadManualLedgerAccounts(next).catch(() => undefined);
              }}
            />
          </div>
          <div>
            <Typography.Text>币种</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={manualLedgerCurrency}
              options={manualLedgerCurrencyOptions.map((c) => ({
                value: c,
                label: formatCurrencyWithSymbol(c),
              }))}
              placeholder="请选择币种"
              onChange={(v) => {
                setManualLedgerCurrency(String(v || '').toUpperCase());
              }}
            />
          </div>
          <div>
            <Typography.Text>交易方向</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={manualLedgerDirection}
              options={[
                { value: 'credit', label: '增加' },
                { value: 'debit', label: '减少' },
              ]}
              onChange={(v) => setManualLedgerDirection(v)}
            />
          </div>
          <div>
            <Typography.Text>金额</Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0.01}
              precision={2}
              step={0.01}
              value={manualLedgerAmount}
              onChange={(v) => setManualLedgerAmount(typeof v === 'number' ? v : null)}
              placeholder="请输入金额"
            />
          </div>
          <div>
            <Typography.Text>调整类型</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={manualLedgerReasonType}
              options={[
                { value: 'rebate', label: '返利入账' },
                { value: 'refund', label: '返还款项' },
                { value: 'compensation', label: '赔付款项' },
                { value: 'correction', label: '人工纠正' },
                { value: 'manual_adjustment', label: '其他调整' },
              ]}
              onChange={(v) => setManualLedgerReasonType(String(v))}
            />
          </div>
          <div>
            <Typography.Text>备注</Typography.Text>
            <Input.TextArea
              rows={3}
              style={{ marginTop: 8 }}
              value={manualLedgerRemark}
              onChange={(e) => setManualLedgerRemark(e.target.value)}
              placeholder="请填写调整说明（必填）"
            />
          </div>
          <Alert
            type="warning"
            showIcon
            message="手动调整支持透支：减少方向可使可用余额为负数（表示客户欠款）。"
          />
        </Space>
      </Modal>

      <Modal
        open={manualLedgerConfirmOpen}
        title="确认调整余额"
        okText="确认提交"
        cancelText="返回修改"
        confirmLoading={manualLedgerSubmitting}
        onCancel={() => setManualLedgerConfirmOpen(false)}
        onOk={submitManualLedger}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Typography.Text>
            客户：{manualLedgerCustomerId ? (customerLabelMap[Number(manualLedgerCustomerId)] || `客户#${manualLedgerCustomerId}`) : '-'}
          </Typography.Text>
          <Typography.Text>
            币种账户：{selectedManualLedgerAccount
              ? `${formatCurrencyWithSymbol(selectedManualLedgerAccount.currency)}（自动匹配账户#${selectedManualLedgerAccount.id}）`
              : `${formatCurrencyWithSymbol(manualLedgerCurrency)}（自动创建/匹配）`}
          </Typography.Text>
          <Typography.Text>
            调整方向：{manualLedgerDirection === 'credit' ? '增加' : '减少'}
          </Typography.Text>
          <Typography.Text>
            调整前可用余额：{manualLedgerPreview.before.toFixed(2)} {manualLedgerPreview.currency}
          </Typography.Text>
          <Typography.Text>
            本次调整金额：
            <span style={{ color: manualLedgerPreview.delta >= 0 ? '#389e0d' : '#cf1322', marginLeft: 6 }}>
              {manualLedgerPreview.delta >= 0 ? '+' : ''}{manualLedgerPreview.amount.toFixed(2)} {manualLedgerPreview.currency}
            </span>
          </Typography.Text>
          <Typography.Text strong>
            调整后可用余额预估：{manualLedgerPreview.after.toFixed(2)} {manualLedgerPreview.currency}
          </Typography.Text>
          <Alert
            type="warning"
            showIcon
            message="确认后将立即创建交易记录并更新账户余额，请核对后提交。"
          />
        </Space>
      </Modal>
    </Space>
  );
};

export default FundsPage;

